import {
  getAssignmentById as requestAssignment, getConsumptionByVoyageReport as requestConsumption,
  getSubmittedReportsFull, getCiiVoyageRowsByReportIds, getCiiRuleSet, getCiiVesselSetting,
  CiiVoyageRowDto, CiiShipTypeRuleDto, CiiFuelFactorDto,
} from '../../core/_requests'
import { getGeneralInfoOfVessel } from '../../../Management/core/_requests'
import { calculateAnnualAttainedCii, getFinalCalculatedCiiRating } from '../../core/ciiFinalRating'

interface ReportFieldValueFullDto {
    field: {
        label: string;
    };
    valueText: string;
}

interface ReportDtoFull {
    id?: number;
    reportType?: string;
    status?: string;
    assignment?: { id?: number };
    voyage?: number;
    vesselId?: number;
    hasWarnings?: boolean;
    noOfWarnings?: number;
    createdDateTime: string;
    submittedDateTime?: string;
    values: ReportFieldValueFullDto[];
}

interface AggregatedVoyageData {
    fromDate: string;
    toDate: string;
    sourceDateTime?: string;
    distance: number;
    hoursAtSea: number;
    hoursAtAnchor: number;
    cargoCarried: number;
    cargoWeightProvided?: boolean;
    meHsfo: number;
    aeHsfo: number;
    boilerHsfo: number;
    meMgo: number;
    aeMgo: number;
    boilerMgo: number;
    totalFuel: number;
    totalCo2: number;
    bunkerReceivedHfo: number;
    bunkerReceivedMgo: number;
    fuelBreakdown?: FuelConsumptionMap;
    correctionBreakdown?: FuelCorrectionMap;
    correctionSourceByFuel?: FuelCorrectionSourceMap;
    unmappedFuelTypes?: string[];
    reportName?: string;
    bunkerReportName?: string;
    hfoRob?: number;
    mgoRob?: number;
    cargoHandled: number;
    fcVoyageHsfo?: number | string;
    fcVoyageMgo?: number | string;
    fcElectricalHsfo?: number | string;
    fcElectricalMgo?: number | string;
    fcOtherHsfo?: number | string;
    fcOtherMgo?: number | string;
    tfHsfo?: number | string;
    tfMgo?: number | string;
    distanceAdjustment?: number | string;
    fcBoilerHsfo?: number | string;
    fcBoilerMgo?: number | string;
    reeferKwh?: number | string;
    reeferSfoc?: number | string;
    reeferFuelCode?: string;
    reeferContainersOnboard?: boolean;
    cargoCoolingSystemInUse?: boolean;
    vesselCondition?: string;
    portNameLocation?: string;
    eventType?: string;
    stsVoyage?: string;
    boilerUsed?: string;
    remarks?: string;
    voyageNo?: string;
    leg?: string;
    avgSulphurHsfo?: number | string;
    avgSulphurMgo?: number | string;
    distanceDisplay?: string;
    reportKind?: ReportKind;
    reportDateTime?: string;
    departureDateTime?: string;
    arrivalDateTime?: string;
    departurePortLocation?: string;
    arrivalPortLocation?: string;
    anchorArrival?: boolean;
    departureFromState?: VoyageEventState | null;
    vesselId?: number;
    isBoundaryRow?: boolean;
    reportId?: number;
    reportAssignmentId?: number;
    reportVoyageId?: number;
    reportTypeName?: string;
    reportStatus?: string;
    reportHasWarnings?: boolean;
    reportNoOfWarnings?: number;
}

interface ConsumptionRobBreakdown {
    meHsfo: number;
    aeHsfo: number;
    boilerHsfo: number;
    meMgo: number;
    aeMgo: number;
    boilerMgo: number;
    hfoRob: number;
    mgoRob: number;
    fuels?: FuelConsumptionMap;
    correctionsByFuel?: FuelCorrectionMap;
    unmappedFuelTypes?: string[];
}

interface FuelConsumptionBreakdown {
    me: number;
    ae: number;
    boiler: number;
    other: number;
    rob: number;
}

type FuelConsumptionMap = Record<string, FuelConsumptionBreakdown>;

interface FuelCorrectionBreakdown {
    fcElectrical: number;
    fcBoiler: number;
    fcOther: number;
}

interface FuelCorrectionSource {
    fcElectrical?: 'Auto' | 'Saved';
    fcBoiler?: 'Auto' | 'Saved';
    fcOther?: 'Auto' | 'Saved';
}

type FuelCorrectionMap = Record<string, FuelCorrectionBreakdown>;

type FuelCorrectionSourceMap = Record<string, FuelCorrectionSource>;

type VoyageEventState = 'SAILING' | 'AT_ANCHOR' | 'AT_BERTH';

type ReportKind =
    | 'ARRIVAL'
    | 'DEPARTURE'
    | 'COSP'
    | 'EOSP'
    | 'NOON_SEA'
    | 'NOON_ANCHOR'
    | 'NOON_BERTH'
    | 'OTHER';

const CARBON_FACTORS = {
    HSFO: 3.114,
    MGO: 3.206,
    LFO: 3.1510,
};


export interface DashboardCiiResult {
  vesselId: number
  vesselName: string
  year: number
  rating: string | null
  attainedCii: number | null
  asOf: string | null
  reason?: string
}

export interface DashboardCiiInput {
  vessel: any
  year: number
  reports: any[]
  dpr: any[]
  voyages: any[]
  cargo: any[]
}

// shortcut: aggregation mirrors CiiSummary until its read model is shared;
// source-parity tests detect drift, while final-rating math is already shared.
export async function loadDashboardFinalCii(input: DashboardCiiInput): Promise<DashboardCiiResult> {
  const selectedVesselId = Number(input.vessel.id)
  const selectedYear = input.year
  const vessels = [input.vessel]
  const from = `${selectedYear}-01-01`
  const to = `${selectedYear}-12-31`
  const result: DashboardCiiResult = { vesselId: selectedVesselId, vesselName: input.vessel.fleet_name || input.vessel.name || 'Vessel name unavailable', year: selectedYear, rating: null, attainedCii: null, asOf: null }
  if (!Number.isSafeInteger(selectedVesselId) || selectedVesselId <= 0 || !Number.isInteger(selectedYear) || selectedYear < 1900 || selectedYear > 9999) throw new Error('Select a valid vessel and reporting year.')
  const scopedReports = input.reports.filter(r => Number(r.vesselId) === selectedVesselId && String(r.status).toUpperCase() === 'SUBMITTED')
  const [generalInfo, vesselCiiSetting, ciiRuleSet, fullReports] = await Promise.all([
    getGeneralInfoOfVessel(selectedVesselId).catch(error => { if (error?.message === 'Not found') return null; throw new Error(`Vessel capacity could not be loaded: ${error.message || 'Retry loading the dashboard.'}`) }), getCiiVesselSetting(selectedVesselId), getCiiRuleSet(),
    getSubmittedReportsFull(from, to, selectedVesselId),
  ])
  if (!Array.isArray(fullReports)) throw new Error('The CII report data is invalid. Retry loading the dashboard.')
  const selectedCiiShipType = vesselCiiSetting?.shipTypeCode || ''
  const assignmentCache = new Map<number, any>()
  const assignmentRequests = new Map<number, Promise<any>>()
  const sourceFailures: Error[] = []
  const getAssignmentById = async (id: number) => {
    try {
      if (!assignmentRequests.has(id)) assignmentRequests.set(id, requestAssignment(id))
      return await assignmentRequests.get(id)
    }
    catch (error) { sourceFailures.push(error instanceof Error ? error : new Error('Report template could not be loaded.')); throw error }
  }
  const getConsumptionByVoyageReport = async (voyageId: number, reportId: number) => {
    try { return await requestConsumption(voyageId, reportId) }
    catch (error) { sourceFailures.push(error instanceof Error ? error : new Error('Fuel consumption could not be loaded.')); throw error }
  }
  let aggregatedData: AggregatedVoyageData[] = []
  const setAggregatedData = (rows: AggregatedVoyageData[]) => { aggregatedData = rows }

  const getFieldLabelMapFromAssignment = (assignment: any) => {
        const fieldLabelMap = new Map<number, string>();
        const template = assignment?.template || assignment?.customTemplate;
        const menus = Array.isArray(template?.menus) ? template.menus : [];

        menus.forEach((menu: any) => {
            (menu?.submenus || []).forEach((submenu: any) => {
                (submenu?.fields || []).forEach((field: any) => {
                    if (field?.id != null) {
                        fieldLabelMap.set(Number(field.id), field.label || '');
                    }
                });
            });
        });

        return fieldLabelMap;
    };

  const enrichCreatedReportsWithAssignmentLabels = async (reports: any[]) => {
        return Promise.all(
            reports.map(async (report: any) => {
                const assignmentId = Number(report?.assignment?.id);
                let assignment = assignmentId ? assignmentCache.get(assignmentId) : null;

                if (!assignment && assignmentId) {
                    assignment = await getAssignmentById(assignmentId).catch(() => null);
                    if (assignment) assignmentCache.set(assignmentId, assignment);
                }

                const fieldLabelMap = getFieldLabelMapFromAssignment(assignment);

                return {
                    id: report.id,
                    reportType: report.reportType,
                    status: report.status,
                    assignment: report.assignment,
                    voyage: report.voyage,
                    vesselId: report.vesselId,
                    hasWarnings: report.hasWarnings,
                    noOfWarnings: report.noOfWarnings,
                    createdDateTime: report.createdDateTime,
                    submittedDateTime: report.submittedDateTime,
                    values: Array.isArray(report.values)
                        ? report.values.map((value: any) => ({
                            field: {
                                label: fieldLabelMap.get(Number(value.field)) || value.label || '',
                            },
                            valueText: value.valueText || '',
                        }))
                        : [],
                } as ReportDtoFull;
            })
        );
    };

  const getCargoWeightFromReport = (report?: ReportDtoFull): number | null => {
        if (!report?.values?.length) return null;

        const exact =
            findFieldTextExact(report.values, ['Cargo Weight (MT)']) ||
            findFieldTextExact(report.values, ['Cargo Weight (MTs)']) ||
            findFieldTextExact(report.values, ["Cargo Weight (MT's)"]);

        if (exact !== '') {
            const parsed = parseFloat(exact);
            return Number.isFinite(parsed) ? parsed : null;
        }

        const fuzzy = findFieldText(report.values, [
            'cargo weight',
            'cargo wt',
            'cargo quantity',
            "cargo carried",
        ]);

        if (fuzzy !== '') {
            const parsed = parseFloat(fuzzy);
            return Number.isFinite(parsed) ? parsed : null;
        }

        return null;
    };

  const toDateOnly = (value?: string) => {
        if (!value) return '';
        return value.includes('T') ? value.split('T')[0] : value;
    };

  const toIsoDateOnly = (value?: string) => {
        if (!value) return '';

        const raw = String(value).trim();
        if (!raw) return '';

        const directDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (directDateMatch) {
            return `${directDateMatch[1]}-${directDateMatch[2]}-${directDateMatch[3]}`;
        }

        const nativeParsed = new Date(raw);
        if (!Number.isNaN(nativeParsed.getTime())) {
            return nativeParsed.toISOString().split('T')[0];
        }

        const dmyMatch = raw.match(/^(\d{1,2})[/-](\d{1,2}|[A-Za-z]{3})[/-](\d{2,4})/);
        if (!dmyMatch) return '';

        const [, dayText, monthText, yearText] = dmyMatch;
        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const day = Number(dayText);
        const month = /^\d+$/.test(monthText) ? Number(monthText) - 1 : monthNames.indexOf(monthText.toLowerCase());
        let year = Number(yearText);
        if (yearText.length === 2) year += year >= 70 ? 1900 : 2000;
        if (month < 0 || month > 11 || !day || !year) return '';

        const parsed = new Date(Date.UTC(year, month, day));
        if (Number.isNaN(parsed.getTime())) return '';
        return parsed.toISOString().split('T')[0];
    };

  const findFieldText = (values: ReportFieldValueFullDto[] = [], labelParts: string[]) => {
        const match = values.find(v =>
            labelParts.some(labelPart =>
                v.field.label.toLowerCase().includes(labelPart.toLowerCase())
            )
        );
        return match?.valueText?.trim() || '';
    };

  const findFieldTextExact = (values: ReportFieldValueFullDto[] = [], labels: string[]) => {
        const normalizeExactLabel = (label: string) =>
            label.trim().toLowerCase().replace(/:$/, '');

        const normalizedLabels = labels.map(normalizeExactLabel);
        const match = values.find(v =>
            normalizedLabels.includes(normalizeExactLabel(v.field.label || ''))
        );
        return match?.valueText?.trim() || '';
    };

  const hasFieldWithExactLabel = (values: ReportFieldValueFullDto[] = [], labels: string[]) => {
        const normalizeExactLabel = (label: string) =>
            label.trim().toLowerCase().replace(/:$/, '');

        const normalizedLabels = labels.map(normalizeExactLabel);
        return values.some((value) => normalizedLabels.includes(normalizeExactLabel(value.field.label || '')));
    };

  const getCheckboxFieldState = (report: ReportDtoFull | undefined, labels: string[]) =>
        hasFieldWithExactLabel(report?.values || [], labels) && Boolean(findFieldTextExact(report?.values || [], labels));

  const normalizeVesselCondition = (value?: string) => {
        const normalized = (value || '').trim().toLowerCase();
        if (!normalized) return '';

        if (normalized === 'b' || normalized.includes('ballast')) {
            return 'B';
        }

        if (
            normalized === 'l' ||
            normalized.includes('loaded') ||
            normalized.includes('laden')
        ) {
            return 'L';
        }

        return '';
    };

  const getVesselConditionFromReport = (report?: ReportDtoFull, fallback?: string) => {
        const storedVesselCondition =
            findFieldTextExact(report?.values || [], ['Vessel Condition']) ||
            findFieldText(report?.values || [], [
                'vessel condition',
                'loading condition',
                'ballast / laden',
                'ballast/laden',
            ]) ||
            fallback;

        return normalizeVesselCondition(storedVesselCondition);
    };

  const getEventTypeFromReport = (report?: ReportDtoFull, fallback?: string) =>
        findFieldTextExact(report?.values || [], ['Event Name']) ||
        findFieldText(report?.values || [], ['event name']) ||
        fallback ||
        '';

  const normalizeTextForMatch = (value?: string) =>
        String(value || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();

  const normalizeShipTypeCode = (value?: string) => {
        const normalized = normalizeTextForMatch(value).replace(/\s+/g, '_').toUpperCase();
        if (!normalized) return 'TANKER';
        if (normalized.includes('BULK')) return 'BULK_CARRIER';
        if (normalized.includes('GAS') && !normalized.includes('LNG')) return 'GAS_CARRIER';
        if (normalized.includes('TANK')) return 'TANKER';
        if (normalized.includes('CONTAINER')) return 'CONTAINER_SHIP';
        if (normalized.includes('GENERAL') && normalized.includes('CARGO')) return 'GENERAL_CARGO_SHIP';
        if (normalized.includes('REFRIGERATED') || normalized.includes('REEFER')) return 'REFRIGERATED_CARGO_CARRIER';
        if (normalized.includes('COMBINATION')) return 'COMBINATION_CARRIER';
        if (normalized.includes('LNG')) return 'LNG_CARRIER';
        if (normalized.includes('VEHICLE')) return 'RO_RO_CARGO_SHIP_VEHICLE_CARRIER';
        if (normalized.includes('RO') && normalized.includes('PASSENGER')) return 'RO_RO_PASSENGER_SHIP';
        if (normalized.includes('RO') && normalized.includes('CARGO')) return 'RO_RO_CARGO_SHIP';
        if (normalized.includes('CRUISE')) return 'CRUISE_PASSENGER_SHIP';
        return normalized;
    };

  const parseBooleanLike = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        if (!normalized) return false;
        return (
            normalized === 'true' ||
            normalized === 'yes' ||
            normalized === 'y' ||
            normalized === '1' ||
            normalized === 'on' ||
            normalized === 'checked' ||
            normalized === 'selected'
        );
    };

  const getPortValueFromReport = (values: ReportFieldValueFullDto[] = [], labelParts: string[]) => {
        const matches = values.filter((v) =>
            labelParts.some((labelPart) =>
                v.field.label.toLowerCase().includes(labelPart.toLowerCase())
            )
        );

        for (const match of matches) {
            const valueText = String(match.valueText || '').trim();
            if (!valueText) continue;

            if (parseBooleanLike(valueText)) continue;

            const valueNormalized = normalizeTextForMatch(valueText);
            if (!valueNormalized) continue;

            const labelNormalized = normalizeTextForMatch(match.field.label || '');
            if (labelNormalized && valueNormalized === labelNormalized) continue;

            return valueText;
        }

        return '';
    };

  const getReportKindFromReport = (report?: ReportDtoFull): ReportKind => {
        const reportTypeText = normalizeTextForMatch(report?.reportType || '');
        const eventType = getEventTypeFromReport(report);
        const eventTypeText = normalizeTextForMatch(eventType);

        if (reportTypeText.includes('cosp')) return 'COSP';
        if (reportTypeText.includes('eosp')) return 'EOSP';
        if (reportTypeText.includes('arrival')) return 'ARRIVAL';
        if (reportTypeText.includes('departure')) return 'DEPARTURE';

        if (reportTypeText.includes('noon') || eventTypeText.includes('noon')) {
            // Explicit report type wins.
            if (
                reportTypeText.includes('noon at sea') ||
                reportTypeText.includes('noon sea')
            ) {
                return 'NOON_SEA';
            }

            if (
                reportTypeText.includes('noon at anchor') ||
                reportTypeText.includes('noon anchor')
            ) {
                return 'NOON_ANCHOR';
            }

            if (
                reportTypeText.includes('noon at port') ||
                reportTypeText.includes('noon port') ||
                reportTypeText.includes('noon at berth') ||
                reportTypeText.includes('noon berth')
            ) {
                return 'NOON_BERTH';
            }

            // Prefer explicit checkbox/state flags first (exclude drifting to avoid forcing anchor).
            if (isCheckedEventFlag(report, ['anchor arrival', 'at anchor', 'anchorage'])) {
                return 'NOON_ANCHOR';
            }

            // Keep berth detection narrow here: generic location fields like
            // "Port Name / Location" should not force a noon report into berth.
            if (isCheckedEventFlag(report, ['port arrival']) || eventTypeText.includes('berth') || isBerthEventType(eventType)) {
                return 'NOON_BERTH';
            }

            // Fallback to event name text
            if (eventTypeText.includes('anchor') || isAnchorEventType(eventType)) return 'NOON_ANCHOR';
            if (eventTypeText.includes('berth') || eventTypeText.includes('port') || isBerthEventType(eventType)) {
                return 'NOON_BERTH';
            }

            return 'NOON_SEA';
        }

        return 'OTHER';
    };

  const isCheckedEventFlag = (report: ReportDtoFull | undefined, tokens: string[]) => {
        if (!report?.values?.length) return false;

        return report.values.some((value) => {
            const fieldLabel = normalizeTextForMatch(value?.field?.label || '');
            const fieldMatches = tokens.some((token) => fieldLabel.includes(normalizeTextForMatch(token)));
            if (!fieldMatches) return false;

            const valueText = String(value?.valueText || '').trim();
            if (parseBooleanLike(valueText)) return true;

            // Some templates store checkbox-like values as text arrays/labels.
            const normalizedValueText = normalizeTextForMatch(valueText);
            const normalizedParts = normalizedValueText.split(' ').filter(Boolean);
            if (
                normalizedValueText === '0' ||
                normalizedValueText === 'no' ||
                normalizedValueText === 'false' ||
                normalizedParts.includes('false') ||
                normalizedParts.includes('no')
            ) {
                return false;
            }
            return (
                normalizedValueText.includes('true') ||
                normalizedValueText.includes('yes') ||
                tokens.some((token) => normalizedValueText.includes(normalizeTextForMatch(token)))
            );
        });
    };

  const getArrivalNextStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackEventType?: string,
        fallbackState: VoyageEventState = 'SAILING'
    ): VoyageEventState => {
        if (
            isCheckedEventFlag(report, ['anchor arrival', 'at anchor'])
        ) {
            return 'AT_ANCHOR';
        }

        if (isCheckedEventFlag(report, ['port arrival'])) {
            return 'AT_BERTH';
        }

        if (isCheckedEventFlag(report, ['sts arrival'])) {
            return 'SAILING';
        }

        // Fallback for legacy data where checkboxes may not exist.
        if (isAnchorEventType(fallbackEventType)) return 'AT_ANCHOR';
        if (isBerthEventType(fallbackEventType)) return 'AT_BERTH';
        if (isSailingEventType(fallbackEventType)) return 'SAILING';
        return fallbackState;
    };

  const getDeparturePrevStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackState: VoyageEventState = 'SAILING'
    ): VoyageEventState => {
        if (isCheckedEventFlag(report, ['departure anchor', 'at anchor'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['departure port'])) return 'AT_BERTH';
        if (isCheckedEventFlag(report, ['departure for berth shifting'])) return 'AT_BERTH';
        return fallbackState;
    };

  const getNoonEventStateFromEventFlags = (
        report: ReportDtoFull | undefined,
        fallbackState: VoyageEventState,
        defaultState: VoyageEventState
    ): VoyageEventState => {
        if (!report) return fallbackState || defaultState;

        if (isCheckedEventFlag(report, ['anchor arrival', 'at anchor', 'anchorage'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['sts'])) return 'AT_ANCHOR';
        if (isCheckedEventFlag(report, ['port arrival'])) return 'AT_BERTH';

        return fallbackState || defaultState;
    };

  const resolveRowAndNextEventState = (
        reportKind: ReportKind,
        currentState: VoyageEventState,
        report?: ReportDtoFull,
        rawEventType?: string
    ): { rowState: VoyageEventState; nextState: VoyageEventState } => {
        if (reportKind === 'NOON_SEA') {
            const hasAnchorFlag = isCheckedEventFlag(report, ['anchor arrival', 'at anchor']);

            if (hasAnchorFlag) {
                return { rowState: 'AT_ANCHOR', nextState: 'AT_ANCHOR' };
            }

            if (isCheckedEventFlag(report, ['port arrival'])) {
                return { rowState: 'AT_BERTH', nextState: 'AT_BERTH' };
            }

            return { rowState: 'SAILING', nextState: 'SAILING' };
        }

        if (reportKind === 'NOON_ANCHOR' || reportKind === 'NOON_BERTH') {
            const defaultState: VoyageEventState =
                reportKind === 'NOON_ANCHOR'
                    ? 'AT_ANCHOR'
                    : 'AT_BERTH';
            const noonState = getNoonEventStateFromEventFlags(report, defaultState, defaultState);
            return { rowState: noonState, nextState: noonState };
        }

        if (reportKind === 'ARRIVAL') {
            const arrivalNextState = getArrivalNextStateFromEventFlags(report, rawEventType, currentState);
            const arrivalRowState = currentState === 'SAILING' ? 'SAILING' : arrivalNextState;
            return {
                rowState: arrivalRowState,
                nextState: arrivalNextState,
            };
        }

        if (reportKind === 'DEPARTURE') {
            return {
                rowState: currentState === 'SAILING' ? 'SAILING' : currentState,
                nextState: 'SAILING',
            };
        }

        if (reportKind === 'EOSP') {
    // EOSP belongs to the current voyage's last active event.
    // If vessel is at berth after arrival + noon at port flow,
    // EOSP should stay in AT_BERTH and must not create sailing.
    return {
        rowState: currentState,
        nextState: currentState,
    };
}

        if (reportKind === 'COSP') {
            // COSP belongs to previous voyage's last event, but it also starts the next voyage flow.
            return {
                rowState: currentState,
                nextState: 'SAILING' as VoyageEventState,
            };
        }

        return {
            rowState: currentState,
            nextState: currentState,
        };
    };

  const isAnchorEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return normalized.includes('anchor') || normalized.includes('anchorage');
    };

  const isBerthEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return (
            normalized === 'b' ||
            normalized.includes('berth') ||
            normalized.includes('in port') ||
            normalized.includes('at port') ||
            /\bport\b/.test(normalized)
        );
    };

  const isSailingEventType = (value?: string) => {
        const normalized = normalizeTextForMatch(value);
        return (
            normalized.includes('sailing') ||
            normalized.includes('at sea') ||
            normalized.includes('sea passage') ||
            normalized.includes('underway') ||
            normalized.includes('under way') ||
            normalized.includes('drifting') ||
            normalized.includes('departure') ||
            normalized.includes('cosp') ||
            normalized.includes('eosp')
        );
    };

  const resolveVoyageEventState = (
        row: Pick<AggregatedVoyageData, 'reportName' | 'eventType' | 'vesselCondition'>,
        previousState?: VoyageEventState
    ): VoyageEventState => {
        const reportName = normalizeTextForMatch(row.reportName);
        const eventType = normalizeTextForMatch(row.eventType);
        const vesselCondition = normalizeTextForMatch(row.vesselCondition);
        const combinedEventText = `${eventType} ${vesselCondition}`.trim();

        if (isAnchorEventType(combinedEventText)) return 'AT_ANCHOR';
        if (isBerthEventType(combinedEventText)) return 'AT_BERTH';
        if (isSailingEventType(combinedEventText)) return 'SAILING';

        if (reportName.includes('arrival')) {
            if (isAnchorEventType(`${reportName} ${combinedEventText}`)) return 'AT_ANCHOR';
            if (isBerthEventType(`${reportName} ${combinedEventText}`)) return 'AT_BERTH';
            return previousState === 'AT_ANCHOR' ? 'AT_ANCHOR' : 'AT_BERTH';
        }

        if (reportName.includes('cosp')) {
    return 'SAILING';
}

        if (isAnchorEventType(reportName)) return 'AT_ANCHOR';
        if (isBerthEventType(reportName)) return 'AT_BERTH';
        if (isSailingEventType(reportName) || reportName.includes('noon')) return 'SAILING';

        return previousState || 'SAILING';
    };

  const toVoyageEventLabel = (state: VoyageEventState) => {
        if (state === 'AT_ANCHOR') return 'AT ANCHOR';
        if (state === 'AT_BERTH') return 'AT BERTH / STS TRANSFER';
        return 'SAILING';
    };

  const isReportNameLike = (reportName: string | undefined, token: string) =>
        normalizeTextForMatch(reportName).includes(token);

  const isArrivalReportName = (reportName?: string) => isReportNameLike(reportName, 'arrival');

  const isDepartureOrCospReportName = (reportName?: string) =>
        isReportNameLike(reportName, 'departure') ||
        isReportNameLike(reportName, 'cosp') ||
        isReportNameLike(reportName, 'eosp');

  const isArrivalKind = (row?: Pick<AggregatedVoyageData, 'reportName' | 'reportKind'>) =>
        row?.reportKind === 'ARRIVAL' || isArrivalReportName(row?.reportName);

  const isDepartureOrCospKind = (row?: Pick<AggregatedVoyageData, 'reportName' | 'reportKind'>) =>
        row?.reportKind === 'DEPARTURE' ||
        row?.reportKind === 'COSP' ||
        row?.reportKind === 'EOSP' ||
        isDepartureOrCospReportName(row?.reportName);

  const getDateTimeFieldValueFromReport = (report?: ReportDtoFull, _labelParts: string[] = []) => {
        if (!report) return '';
        return buildReportUtcDateTime(report);
    };

  const formatLegNumberDisplay = (seq?: number | string | null) => {
        if (seq == null || seq === '') return '';
        const trimmed = String(seq).trim();
        if (!trimmed || trimmed === '--') return '';
        if (/^L\d+$/i.test(trimmed)) return trimmed.toUpperCase();
        if (/^\d+$/.test(trimmed)) return `L${trimmed}`;
        return trimmed;
    };

  const resolveLegDisplay = (
        report?: ReportDtoFull,
        matchingVoyage?: any,
        persistedCiiRow?: CiiVoyageRowDto
    ): string => {
        const repSeq =
            (report as any)?.legDto?.sequenceNumber ??
            (report as any)?.legSequenceNumber ??
            (report as any)?.leg?.sequenceNumber;
        if (repSeq != null) {
            const formatted = formatLegNumberDisplay(repSeq);
            if (formatted) return formatted;
        }

        const reportLegId =
            (report as any)?.legId ??
            (report as any)?.legDto?.id ??
            (report as any)?.leg?.id ??
            (persistedCiiRow as any)?.legId ??
            matchingVoyage?.legID ??
            matchingVoyage?.legId;

        if (reportLegId != null && matchingVoyage?.legs?.length) {
            const matchedLeg = matchingVoyage.legs.find(
                (l: any) => Number(l.id) === Number(reportLegId)
            );
            if (matchedLeg?.sequenceNumber != null) {
                const formatted = formatLegNumberDisplay(matchedLeg.sequenceNumber);
                if (formatted) return formatted;
            }
        }

        const dynamicLegField = findFieldText(report?.values || [], ['leg no', 'leg number', 'leg id', 'leg']);
        if (dynamicLegField) {
            const formatted = formatLegNumberDisplay(dynamicLegField);
            if (formatted) return formatted;
        }

        if (matchingVoyage?.legs?.length === 1 && matchingVoyage.legs[0]?.sequenceNumber != null) {
            const formatted = formatLegNumberDisplay(matchingVoyage.legs[0].sequenceNumber);
            if (formatted) return formatted;
        }

        return '';
    };

  /* eslint-disable no-useless-escape -- Keep the source CII date parser unchanged for parity checks. */
  const normalizeDateTimeForParse = (value?: string) => {
        if (!value) return '';
        const trimmed = String(value).trim();
        if (!trimmed) return '';

        const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
        if (hasTimezone) return trimmed;

        // Date-only: YYYY-MM-DD
        const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
        if (dateOnlyMatch) {
            return `${trimmed}T00:00:00Z`;
        }

        // Date with 12-hour AM/PM: YYYY-MM-DD hh:mm(:ss)? AM/PM
        const ampmMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i.exec(trimmed);
        if (ampmMatch) {
            const [, y, m, d, hStr, minStr, secStr = '00', meridiem] = ampmMatch;
            let hour = parseInt(hStr, 10);
            if (meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
            if (meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;
            const hourPad = String(hour).padStart(2, '0');
            return `${y}-${m}-${d}T${hourPad}:${minStr}:${secStr}Z`;
        }

        // Date with 24-hour: YYYY-MM-DD HH:mm(:ss)?
        const dateTimeMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
        if (dateTimeMatch) {
            const [, y, m, d, hStr, minStr, secStr = '00'] = dateTimeMatch;
            const hourPad = String(parseInt(hStr, 10)).padStart(2, '0');
            return `${y}-${m}-${d}T${hourPad}:${minStr}:${secStr}Z`;
        }

        // Day-first: DD/MM/YYYY or DD-MM-YYYY HH:mm(:ss)? (with optional AM/PM)
        const dmyMatch = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i.exec(trimmed);
        if (dmyMatch) {
            const [, dStr, mStr, yStr, hStr = '0', minStr = '00', secStr = '00', meridiem] = dmyMatch;
            let hour = parseInt(hStr, 10);
            if (meridiem && meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
            if (meridiem && meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;
            const dayPad = String(parseInt(dStr, 10)).padStart(2, '0');
            const monthPad = String(parseInt(mStr, 10)).padStart(2, '0');
            const hourPad = String(hour).padStart(2, '0');
            return `${yStr}-${monthPad}-${dayPad}T${hourPad}:${minStr}:${secStr}Z`;
        }

        return trimmed;
    };

  /* eslint-enable no-useless-escape */
  const toDateTimeMs = (value?: string) => {
        if (!value) return Number.NaN;
        const normalized = normalizeDateTimeForParse(value);
        const parsed = new Date(normalized).getTime();
        return Number.isNaN(parsed) ? Number.NaN : parsed;
    };

  const findMatchingVoyageByDate = (reportDate: string, voyages: any[]) => {
        if (!reportDate) return null;
        const reportTime = toDateTimeMs(reportDate);
        if (Number.isNaN(reportTime)) return null;

        const exactEndMatch = (voyages || [])
            .filter((v: any) => v?.endDate && toDateTimeMs(v.endDate) === reportTime)
            .sort((a: any, b: any) => {
                const aTime = toDateTimeMs(a?.startDate);
                const bTime = toDateTimeMs(b?.startDate);
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return String(a?.startDate || '').localeCompare(String(b?.startDate || ''));
            });

        if (exactEndMatch.length) {
            return exactEndMatch[0];
        }

        const candidates = (voyages || [])
            .filter((v: any) => v?.startDate)
            .map((v: any) => {
                const startTime = toDateTimeMs(v.startDate);
                const endTime = v?.endDate ? toDateTimeMs(v.endDate) : Number.POSITIVE_INFINITY;
                return { voyage: v, startTime, endTime };
            })
            .filter((v) => !Number.isNaN(v.startTime) && reportTime >= v.startTime && reportTime <= v.endTime)
            .sort((a, b) => b.startTime - a.startTime);

        return candidates.length ? candidates[0].voyage : null;
    };

  const buildVoyageSequence = (voyages: any[]) => {
        const sorted = [...(voyages || [])]
            .filter((v: any) => v?.voyageNumber)
            .sort((a: any, b: any) => {
                const aTime = toDateTimeMs(a?.startDate);
                const bTime = toDateTimeMs(b?.startDate);
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return String(a?.startDate || '').localeCompare(String(b?.startDate || ''));
            });

        const prevByVoyageNo = new Map<string, string>();
        for (let i = 1; i < sorted.length; i += 1) {
            const prevNo = String(sorted[i - 1]?.voyageNumber || '').trim();
            const currNo = String(sorted[i]?.voyageNumber || '').trim();
            if (prevNo && currNo && !prevByVoyageNo.has(currNo)) {
                prevByVoyageNo.set(currNo, prevNo);
            }
        }

        return { sorted, prevByVoyageNo };
    };

  const pickLaterDateTime = (current?: string, candidate?: string) => {
        if (!current) return candidate || '';
        if (!candidate) return current;

        const currentMs = toDateTimeMs(current);
        const candidateMs = toDateTimeMs(candidate);

        if (Number.isNaN(currentMs) && Number.isNaN(candidateMs)) {
            return candidate > current ? candidate : current;
        }
        if (Number.isNaN(currentMs)) return candidate;
        if (Number.isNaN(candidateMs)) return current;
        return candidateMs >= currentMs ? candidate : current;
    };

  const toFiniteNumber = (value: unknown) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

  const parseOptionalNumber = (value?: string | number | null) => {
            if (value === null || value === undefined || value === '') return undefined;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : undefined;
        };

  const numericValue = (value?: string | number | null) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

  const resolveStsVoyageValue = (value?: string | null) => {
        const normalized = (value ?? '').trim().toUpperCase();
        return normalized === 'Y' || normalized === 'N' ? normalized : 'N';
    };

  const getPersistedFuelDetail = (row: CiiVoyageRowDto | undefined, fuelCode: string) =>
        (row?.fuelDetails || []).find((detail) => String(detail.fuelCode || '').toUpperCase() === fuelCode);

  const hasPersistedCorrectionValue = (value?: string | number | null) =>
        value !== null && value !== undefined && String(value).trim() !== '';

  const getReeferElectricalForFuel = (row: AggregatedVoyageData, fuelCode: string) => {
        if (!row.reeferContainersOnboard && !row.cargoCoolingSystemInUse) return 0;
        const selectedFuelCode = getConfiguredFuelCode(row.reeferFuelCode);
        if (!selectedFuelCode || selectedFuelCode !== fuelCode) return 0;
        return numericValue(row.reeferKwh) * numericValue(row.reeferSfoc);
    };

  const getCorrectionValueForFuel = (
        row: AggregatedVoyageData,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => {
        const baseValue =
            fuelCode === 'HSFO' && field === 'fcElectrical' ? (hasPersistedCorrectionValue(row.fcElectricalHsfo) ? numericValue(row.fcElectricalHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcElectrical' ? (hasPersistedCorrectionValue(row.fcElectricalMgo) ? numericValue(row.fcElectricalMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'HSFO' && field === 'fcBoiler' ? (hasPersistedCorrectionValue(row.fcBoilerHsfo) ? numericValue(row.fcBoilerHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcBoiler' ? (hasPersistedCorrectionValue(row.fcBoilerMgo) ? numericValue(row.fcBoilerMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'HSFO' && field === 'fcOther' ? (hasPersistedCorrectionValue(row.fcOtherHsfo) ? numericValue(row.fcOtherHsfo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            fuelCode === 'MGO' && field === 'fcOther' ? (hasPersistedCorrectionValue(row.fcOtherMgo) ? numericValue(row.fcOtherMgo) : numericValue(row.correctionBreakdown?.[fuelCode]?.[field])) :
            numericValue(row.correctionBreakdown?.[fuelCode]?.[field]);
        return baseValue + (field === 'fcElectrical' ? getReeferElectricalForFuel(row, fuelCode) : 0);
    };

  const getPersistedCorrectionNumber = (
        persistedRow: CiiVoyageRowDto | undefined,
        fuelCode: string,
        field: keyof FuelCorrectionBreakdown
    ) => {
        const detail = getPersistedFuelDetail(persistedRow, fuelCode);
        const rawValue =
            field === 'fcElectrical' ? detail?.fcElectrical :
            field === 'fcBoiler' ? detail?.fcBoiler :
            detail?.fcOther;
        return hasPersistedCorrectionValue(rawValue) ? parseOptionalNumber(rawValue) : undefined;
    };

  const buildCorrectionBreakdownForRow = (
        autoCorrections: FuelCorrectionMap | undefined,
        persistedRow?: CiiVoyageRowDto
    ) => {
        const result: FuelCorrectionMap = {};
        const sources: FuelCorrectionSourceMap = {};
        const fuelCodes = new Set([
            ...Object.keys(autoCorrections || {}),
            ...(persistedRow?.fuelDetails || []).map((detail) => String(detail.fuelCode || '').toUpperCase()).filter(Boolean),
        ]);

        fuelCodes.forEach((fuelCode) => {
            const auto = autoCorrections?.[fuelCode] || { fcElectrical: 0, fcBoiler: 0, fcOther: 0 };
            const target = getOrCreateFuelCorrection(result, fuelCode);
            const source = sources[fuelCode] || {};

            (['fcElectrical', 'fcBoiler', 'fcOther'] as Array<keyof FuelCorrectionBreakdown>).forEach((field) => {
                const persistedValue = getPersistedCorrectionNumber(persistedRow, fuelCode, field);
                if (persistedValue !== undefined) {
                    target[field] = persistedValue;
                    source[field] = 'Saved';
                } else {
                    target[field] = auto[field] || 0;
                    if (target[field] > 0) source[field] = 'Auto';
                }
            });

            sources[fuelCode] = source;
        });

        return { corrections: result, sources };
    };

  const getShipTypeIndexByYear = (year: number) => {
        const configuredIndex = ciiRuleSet?.reductionFactors?.find((item) => Number(item.reportingYear) === Number(year))?.correctionYearIndex;
        if (configuredIndex !== undefined && configuredIndex !== null) return Number(configuredIndex) || 0;
        if (!year || Number.isNaN(year)) return 0;
        return Math.max(0, year - 2023);
    };

  const getReductionFactorForYear = (year: number) =>
        (ciiRuleSet?.reductionFactors || []).find((factor) => Number(factor.reportingYear) === Number(year));

  const getRuleCapacity = (rule?: CiiShipTypeRuleDto | null) => {
        if (!rule) return 0;
        const overrideCapacity = Number(vesselCiiSetting?.capacityOverride);
        if (overrideCapacity > 0) return overrideCapacity;
        if (rule.capacityBasis === 'GT') return Number(generalInfo?.grossTonnage) || 0;
        if (rule.capacityBasis === 'FIXED') return Number(rule.fixedCapacity) || Number(generalInfo?.summerDeadweight) || 0;
        return Number(generalInfo?.summerDeadweight) || 0;
    };

  const findActiveCiiRule = () => {
        const shipTypeCode = selectedCiiShipType || normalizeShipTypeCode(vessels.find(v => v.id === selectedVesselId)?.vesselType || 'TANKER');
        const dwt = Number(generalInfo?.summerDeadweight) || 0;
        const candidates = (ciiRuleSet?.shipTypeRules || []).filter((rule) => rule.shipTypeCode === shipTypeCode);
        if (!candidates.length) return null;

        return candidates.find((rule) => {
            const minOk = rule.minDwt === undefined || rule.minDwt === null || dwt >= Number(rule.minDwt);
            const maxOk = rule.maxDwt === undefined || rule.maxDwt === null || dwt < Number(rule.maxDwt);
            return minOk && maxOk;
        }) || candidates[0];
    };

  const getCiiRuleMetrics = () => {
        const rule = findActiveCiiRule();
        const capacity = getRuleCapacity(rule);
        const reduction = getReductionFactorForYear(selectedYear);
        const referenceCii = rule && capacity > 0
            ? Number(rule.referenceA) * Math.pow(capacity, -Number(rule.referenceC))
            : 0;
        const reductionPercent = Number(reduction?.reductionPercent);
        const requiredCii = referenceCii > 0 && Number.isFinite(reductionPercent)
            ? referenceCii * (1 - (reductionPercent / 100))
            : 0;
        const boundaries = rule && requiredCii > 0
            ? {
                a: requiredCii * Number(rule.ratingD1),
                b: requiredCii * Number(rule.ratingD2),
                c: requiredCii * Number(rule.ratingD3),
                d: requiredCii * Number(rule.ratingD4),
            }
            : null;

        return { rule, capacity, reduction, referenceCii, requiredCii, boundaries };
    };

  const getDurationHours = (fromDateTime?: string, toDateTime?: string) => {
        if (!fromDateTime || !toDateTime) return 0;
        const fromMs = toDateTimeMs(fromDateTime);
        const toMs = toDateTimeMs(toDateTime);
        if (Number.isNaN(fromMs) || Number.isNaN(toMs) || toMs <= fromMs) return 0;
        return (toMs - fromMs) / (1000 * 60 * 60);
    };

  const getHoursByEventState = (
        state: VoyageEventState,
        fromDateTime?: string,
        toDateTime?: string
    ) => {
        const durationHours = getDurationHours(fromDateTime, toDateTime);
        if (durationHours <= 0) {
            return { hoursAtSea: 0, hoursAtAnchor: 0 };
        }
        if (state === 'SAILING') {
            return { hoursAtSea: durationHours, hoursAtAnchor: 0 };
        }
        if (state === 'AT_ANCHOR' || state === 'AT_BERTH') {
            return { hoursAtSea: 0, hoursAtAnchor: durationHours };
        }
        return { hoursAtSea: 0, hoursAtAnchor: 0 };
    };

  const mergeVoyageRowsByEvent = (rows: AggregatedVoyageData[]) => {
        if (!rows.length) return rows;

        const sortedRows = [...rows].sort((a, b) => {
            const left = toDateTimeMs(a.sourceDateTime || a.fromDate);
            const right = toDateTimeMs(b.sourceDateTime || b.fromDate);

            if (!Number.isNaN(left) && !Number.isNaN(right)) {
                return left - right;
            }

            return (a.sourceDateTime || a.fromDate || '').localeCompare(b.sourceDateTime || b.fromDate || '');
        });

        const grouped: Array<
            AggregatedVoyageData & {
                __state: VoyageEventState;
                __voyageKey: string;
                __rows: AggregatedVoyageData[];
            }
        > = [];
        let previousState: VoyageEventState | undefined;

        sortedRows.forEach((row) => {
            const eventState = resolveVoyageEventState(row, previousState);
            const voyageKey = String(row.voyageNo || '').trim().toUpperCase() || '__NO_VOYAGE__';
            const lastGroup = grouped[grouped.length - 1];
            const shouldMerge =
                Boolean(lastGroup) &&
                lastGroup.__state === eventState &&
                lastGroup.__voyageKey === voyageKey;

            if (!shouldMerge) {
                grouped.push({
                    ...row,
                    eventType: toVoyageEventLabel(eventState),
                    __state: eventState,
                    __voyageKey: voyageKey,
                    __rows: [row],
                });
                previousState = eventState;
                return;
            }

            const current = lastGroup;
            current.__rows.push(row);
            current.toDate = pickLaterDateTime(current.toDate, row.toDate || row.fromDate);
            current.distance += toFiniteNumber(row.distance);
            current.hoursAtSea += toFiniteNumber(row.hoursAtSea);
            current.hoursAtAnchor += toFiniteNumber(row.hoursAtAnchor);
            if (row.cargoWeightProvided) {
                // Cargo weight is a point-in-time state from the report, not an additive quantity.
                current.cargoCarried = toFiniteNumber(row.cargoCarried);
                current.cargoWeightProvided = true;
            }
            current.meHsfo += toFiniteNumber(row.meHsfo);
            current.aeHsfo += toFiniteNumber(row.aeHsfo);
            current.boilerHsfo += toFiniteNumber(row.boilerHsfo);
            current.meMgo += toFiniteNumber(row.meMgo);
            current.aeMgo += toFiniteNumber(row.aeMgo);
            current.boilerMgo += toFiniteNumber(row.boilerMgo);
            current.totalFuel += toFiniteNumber(row.totalFuel);
            current.totalCo2 += toFiniteNumber(row.totalCo2);
            current.bunkerReceivedHfo += toFiniteNumber(row.bunkerReceivedHfo);
            current.bunkerReceivedMgo += toFiniteNumber(row.bunkerReceivedMgo);
            if (row.fuelBreakdown) {
                current.fuelBreakdown = current.fuelBreakdown || {};
                Object.entries(row.fuelBreakdown).forEach(([fuelCode, fuel]) => {
                    const target = getOrCreateFuelBreakdown(current.fuelBreakdown || {}, fuelCode);
                    target.me += toFiniteNumber(fuel.me);
                    target.ae += toFiniteNumber(fuel.ae);
                    target.boiler += toFiniteNumber(fuel.boiler);
                    target.other += toFiniteNumber(fuel.other);
                    target.rob = toFiniteNumber(fuel.rob) || target.rob;
                });
            }
            if (row.correctionBreakdown) {
                current.correctionBreakdown = current.correctionBreakdown || {};
                current.correctionSourceByFuel = current.correctionSourceByFuel || {};
                Object.entries(row.correctionBreakdown).forEach(([fuelCode, correction]) => {
                    const target = getOrCreateFuelCorrection(current.correctionBreakdown || {}, fuelCode);
                    target.fcElectrical += toFiniteNumber(correction.fcElectrical);
                    target.fcBoiler += toFiniteNumber(correction.fcBoiler);
                    target.fcOther += toFiniteNumber(correction.fcOther);
                    const source = row.correctionSourceByFuel?.[fuelCode];
                    if (source) {
                        current.correctionSourceByFuel![fuelCode] = {
                            ...(current.correctionSourceByFuel![fuelCode] || {}),
                            ...source,
                        };
                    }
                });
            }
            if (row.unmappedFuelTypes?.length) {
                current.unmappedFuelTypes = Array.from(new Set([...(current.unmappedFuelTypes || []), ...row.unmappedFuelTypes]));
            }
            current.cargoHandled += toFiniteNumber(row.cargoHandled);

            if (row.fcVoyageHsfo !== undefined) {
                current.fcVoyageHsfo = toFiniteNumber(current.fcVoyageHsfo) + numericValue(row.fcVoyageHsfo);
            }
            if (row.fcVoyageMgo !== undefined) {
                current.fcVoyageMgo = toFiniteNumber(current.fcVoyageMgo) + numericValue(row.fcVoyageMgo);
            }
            if (row.fcElectricalHsfo !== undefined) {
                current.fcElectricalHsfo = toFiniteNumber(current.fcElectricalHsfo) + numericValue(row.fcElectricalHsfo);
            }
            if (row.fcElectricalMgo !== undefined) {
                current.fcElectricalMgo = toFiniteNumber(current.fcElectricalMgo) + numericValue(row.fcElectricalMgo);
            }
            if (row.fcBoilerHsfo !== undefined) {
                current.fcBoilerHsfo = toFiniteNumber(current.fcBoilerHsfo) + numericValue(row.fcBoilerHsfo);
            }
            if (row.fcBoilerMgo !== undefined) {
                current.fcBoilerMgo = toFiniteNumber(current.fcBoilerMgo) + numericValue(row.fcBoilerMgo);
            }
            if (row.fcOtherHsfo !== undefined) {
                current.fcOtherHsfo = toFiniteNumber(current.fcOtherHsfo) + numericValue(row.fcOtherHsfo);
            }
            if (row.fcOtherMgo !== undefined) {
                current.fcOtherMgo = toFiniteNumber(current.fcOtherMgo) + numericValue(row.fcOtherMgo);
            }
            if (row.tfHsfo !== undefined) {
                current.tfHsfo = toFiniteNumber(current.tfHsfo) + numericValue(row.tfHsfo);
            }
            if (row.tfMgo !== undefined) {
                current.tfMgo = toFiniteNumber(current.tfMgo) + numericValue(row.tfMgo);
            }
            if (row.distanceAdjustment !== undefined) {
                current.distanceAdjustment = toFiniteNumber(current.distanceAdjustment) + numericValue(row.distanceAdjustment);
            }
            if (row.reeferKwh !== undefined) current.reeferKwh = toFiniteNumber(current.reeferKwh) + numericValue(row.reeferKwh);
            if (row.reeferSfoc !== undefined && current.reeferSfoc === undefined) current.reeferSfoc = row.reeferSfoc;
            if (row.reeferFuelCode && !current.reeferFuelCode) current.reeferFuelCode = row.reeferFuelCode;
            current.reeferContainersOnboard = Boolean(current.reeferContainersOnboard || row.reeferContainersOnboard);
            current.cargoCoolingSystemInUse = Boolean(current.cargoCoolingSystemInUse || row.cargoCoolingSystemInUse);

            if (row.hfoRob !== undefined) current.hfoRob = row.hfoRob;
            if (row.mgoRob !== undefined) current.mgoRob = row.mgoRob;
            if (row.avgSulphurHsfo !== undefined) current.avgSulphurHsfo = row.avgSulphurHsfo;
            if (row.avgSulphurMgo !== undefined) current.avgSulphurMgo = row.avgSulphurMgo;

            if (!current.vesselCondition && row.vesselCondition) current.vesselCondition = row.vesselCondition;
            if (!current.portNameLocation && row.portNameLocation) current.portNameLocation = row.portNameLocation;
            if (!current.stsVoyage && row.stsVoyage) current.stsVoyage = row.stsVoyage;
            if (!current.boilerUsed && row.boilerUsed) current.boilerUsed = row.boilerUsed;
            if (!current.remarks && row.remarks) current.remarks = row.remarks;

            if (row.reportId) current.reportId = row.reportId;
            if (row.reportAssignmentId) current.reportAssignmentId = row.reportAssignmentId;
            if (row.reportVoyageId) current.reportVoyageId = row.reportVoyageId;
            if (row.reportTypeName) current.reportTypeName = row.reportTypeName;
            if (row.reportStatus) current.reportStatus = row.reportStatus;
            if (row.reportHasWarnings !== undefined) current.reportHasWarnings = row.reportHasWarnings;
            if (row.reportNoOfWarnings !== undefined) current.reportNoOfWarnings = row.reportNoOfWarnings;

            previousState = eventState;
        });

        grouped.forEach((group) => {
            const arrivalRow = group.__rows.find((row) => isArrivalKind(row));
            const cospOrDepartureRow = group.__rows.find((row) => isDepartureOrCospKind(row));

            if (group.__state === 'SAILING') {
                if (cospOrDepartureRow?.fromDate) {
                    group.fromDate = cospOrDepartureRow.fromDate;
                    group.sourceDateTime = cospOrDepartureRow.sourceDateTime || cospOrDepartureRow.fromDate;
                }
                if (cospOrDepartureRow?.portNameLocation) {
                    group.portNameLocation = cospOrDepartureRow.portNameLocation;
                }
            }

            if ((group.__state === 'AT_BERTH' || group.__state === 'AT_ANCHOR') && arrivalRow) {
                if (arrivalRow.fromDate) {
                    group.fromDate = arrivalRow.fromDate;
                    group.sourceDateTime = arrivalRow.sourceDateTime || arrivalRow.fromDate;
                }
                if (arrivalRow.portNameLocation) {
                    group.portNameLocation = arrivalRow.portNameLocation;
                }
            }

            const computedHours = getHoursByEventState(group.__state, group.fromDate, group.toDate);
            group.hoursAtSea = computedHours.hoursAtSea;
            group.hoursAtAnchor = computedHours.hoursAtAnchor;

            if (group.reportId) {
                const reportMatch = group.__rows.find(
                    (row) => Number(row.reportId) === Number(group.reportId)
                );
                if (reportMatch?.stsVoyage !== undefined) {
                    group.stsVoyage = reportMatch.stsVoyage;
                }
            }
        });

        return grouped.map(({ __state, __voyageKey, __rows, cargoWeightProvided, ...row }) => row);
    };

  const getReportDateOnly = (report?: ReportDtoFull) => {
        const reportDateTime = buildReportUtcDateTime(report);
        return toDateOnly(reportDateTime || report?.submittedDateTime || report?.createdDateTime);
    };

  const getReportSortDateTime = (report?: ReportDtoFull) =>
        buildReportUtcDateTime(report) || report?.submittedDateTime || report?.createdDateTime || '';

  const isBunkerReport = (report?: ReportDtoFull) =>
        (report?.reportType || '').toLowerCase().includes('bunker') ||
        Boolean(findFieldTextExact(report?.values || [], ['Bunker Received Rows'])) ||
        (
            Boolean(findFieldTextExact(report?.values || [], ['Fuel Type'])) &&
            (
                Boolean(findFieldTextExact(report?.values || [], ["HFO (MT's)"])) ||
                Boolean(findFieldTextExact(report?.values || [], ["MGO (MT's)"]))
            )
        );

  const hasMatchingField = (report: ReportDtoFull | undefined, labelParts: string[]) =>
        Boolean(findFieldText(report?.values || [], labelParts));

  const isPositionReportCandidate = (report?: ReportDtoFull) => {
        if (!report || isBunkerReport(report)) return false;

        return (
            hasMatchingField(report, ['report date time']) ||
            hasMatchingField(report, ['hours at sea', 'hours at anchor']) ||
            hasMatchingField(report, ['distance travelled', 'distance traveled']) ||
            hasMatchingField(report, ['distance made good']) ||
            hasMatchingField(report, ['vessel condition', 'loading condition', 'ballast / laden', 'ballast/laden']) ||
            hasMatchingField(report, ['event type', 'port name', 'location', 'voyage no', 'voyage number'])
        );
    };

  const buildReportUtcDateTime = (report?: ReportDtoFull) => {
        if (!report) return '';

        const reportDateTime =
            findFieldTextExact(report.values || [], ['Report Date Time']) ||
            findFieldText(report.values, ['report date time']);
        if (reportDateTime) return reportDateTime;

        const dateTimeUtc =
            findFieldTextExact(report.values || [], ['Date Time (UTC)', 'DateTime (UTC)']) ||
            findFieldText(report.values, ['date time (utc)', 'datetime (utc)', 'date time utc']);
        if (dateTimeUtc) return dateTimeUtc;

        const dateTimeLocal =
            findFieldTextExact(report.values || [], ['Date Time (Local)', 'DateTime (Local)']) ||
            findFieldText(report.values, ['date time (local)', 'datetime (local)', 'date time local']);
        if (dateTimeLocal) return dateTimeLocal;

        const utcDate = findFieldText(report.values, ['date (utc)', 'date utc']);
        const utcTime = findFieldText(report.values, ['time (utc)', 'time utc']);
        if (utcDate && utcTime) return `${utcDate} ${utcTime}`;
        if (utcDate) return utcDate;

        return report.submittedDateTime || report.createdDateTime || '';
    };

  const getQuarterBoundaryMidnightMs = (year: number) => [
        new Date(year, 3, 1, 0, 0, 0, 0).getTime(),
        new Date(year, 6, 1, 0, 0, 0, 0).getTime(),
        new Date(year, 9, 1, 0, 0, 0, 0).getTime(),
        new Date(year + 1, 0, 1, 0, 0, 0, 0).getTime(),
    ].filter((time) => !Number.isNaN(time));

  const getBoundaryMidnightMsFromReports = (reports: ReportDtoFull[], year: number) => {
        if (!reports.length) return [];
        const boundaries = getQuarterBoundaryMidnightMs(year);
        const boundarySet = new Set(boundaries);
        const matched = new Set<number>();

        reports.forEach((report) => {
            const reportDateTime = buildReportUtcDateTime(report);
            const departureDateTime = getDateTimeFieldValueFromReport(report, ['departure']);
            [reportDateTime, departureDateTime].forEach((value) => {
                const time = toDateTimeMs(value);
                if (boundarySet.has(time)) {
                    matched.add(time);
                }
            });
        });

        return boundaries.filter((time) => matched.has(time));
    };

  const formatLocalDateTime = (timeMs: number) => {
        const date = new Date(timeMs);
        const pad = (value: number) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

  const scaleNumeric = (value: number | string | undefined, ratio: number) => {
        if (value === undefined || value === null || value === '') return undefined;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed * ratio : undefined;
    };

  const splitRowByBoundaryMidnights = (row: AggregatedVoyageData, boundaryMs: number[]) => {
        const startMs = toDateTimeMs(row.fromDate);
        const endMs = toDateTimeMs(row.toDate);
        if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return [row];

        const cuts = boundaryMs
            .filter((time) => time > startMs && time < endMs)
            .sort((a, b) => a - b);
        if (!cuts.length) return [row];

        const totalDuration = endMs - startMs;
        const segments: Array<{ start: number; end: number }> = [];
        let segmentStart = startMs;
        cuts.forEach((cut) => {
            segments.push({ start: segmentStart, end: cut });
            segmentStart = cut;
        });
        segments.push({ start: segmentStart, end: endMs });

        return segments.map((segment) => {
            const ratio = (segment.end - segment.start) / totalDuration;
            const scaledFuelBreakdown: FuelConsumptionMap | undefined = row.fuelBreakdown
                ? Object.entries(row.fuelBreakdown).reduce((acc, [fuelCode, fuel]) => {
                    acc[fuelCode] = {
                        me: fuel.me * ratio,
                        ae: fuel.ae * ratio,
                        boiler: fuel.boiler * ratio,
                        other: fuel.other * ratio,
                        rob: fuel.rob,
                    };
                    return acc;
                }, {} as FuelConsumptionMap)
                : undefined;
            const scaledCorrectionBreakdown: FuelCorrectionMap | undefined = row.correctionBreakdown
                ? Object.entries(row.correctionBreakdown).reduce((acc, [fuelCode, correction]) => {
                    acc[fuelCode] = {
                        fcElectrical: correction.fcElectrical * ratio,
                        fcBoiler: correction.fcBoiler * ratio,
                        fcOther: correction.fcOther * ratio,
                    };
                    return acc;
                }, {} as FuelCorrectionMap)
                : undefined;
            return {
                ...row,
                fromDate: formatLocalDateTime(segment.start),
                toDate: formatLocalDateTime(segment.end),
                sourceDateTime: formatLocalDateTime(segment.start),
                distance: row.distance * ratio,
                hoursAtSea: row.hoursAtSea * ratio,
                hoursAtAnchor: row.hoursAtAnchor * ratio,
                  cargoCarried: (row.vesselCondition || '').trim().toUpperCase() === 'L' ? row.cargoCarried : 0,
                cargoHandled: row.cargoHandled * ratio,
                meHsfo: row.meHsfo * ratio,
                aeHsfo: row.aeHsfo * ratio,
                boilerHsfo: row.boilerHsfo * ratio,
                meMgo: row.meMgo * ratio,
                aeMgo: row.aeMgo * ratio,
                boilerMgo: row.boilerMgo * ratio,
                totalFuel: row.totalFuel * ratio,
                totalCo2: row.totalCo2 * ratio,
                bunkerReceivedHfo: row.bunkerReceivedHfo * ratio,
                bunkerReceivedMgo: row.bunkerReceivedMgo * ratio,
                fuelBreakdown: scaledFuelBreakdown,
                correctionBreakdown: scaledCorrectionBreakdown,
                fcVoyageHsfo: scaleNumeric(row.fcVoyageHsfo, ratio),
                fcVoyageMgo: scaleNumeric(row.fcVoyageMgo, ratio),
                fcElectricalHsfo: scaleNumeric(row.fcElectricalHsfo, ratio),
                fcElectricalMgo: scaleNumeric(row.fcElectricalMgo, ratio),
                fcBoilerHsfo: scaleNumeric(row.fcBoilerHsfo, ratio),
                fcBoilerMgo: scaleNumeric(row.fcBoilerMgo, ratio),
                fcOtherHsfo: scaleNumeric(row.fcOtherHsfo, ratio),
                fcOtherMgo: scaleNumeric(row.fcOtherMgo, ratio),
                tfHsfo: scaleNumeric(row.tfHsfo, ratio),
                tfMgo: scaleNumeric(row.tfMgo, ratio),
                distanceAdjustment: scaleNumeric(row.distanceAdjustment, ratio),
                reeferKwh: scaleNumeric(row.reeferKwh, ratio),
                reeferContainersOnboard: row.reeferContainersOnboard,
                cargoCoolingSystemInUse: row.cargoCoolingSystemInUse,
            };
        });
    };

  const splitRowsOnQuarterBoundaries = (
        rows: AggregatedVoyageData[],
        reports: ReportDtoFull[],
        year: number
    ) => {
        const boundaryMs = getBoundaryMidnightMsFromReports(reports, year);
        if (!boundaryMs.length) return rows;
        return rows.flatMap((row) => splitRowByBoundaryMidnights(row, boundaryMs));
    };

  const getDistanceTravelledFromNoonReport = (report?: ReportDtoFull) => {
        if (!report) return null;

        const exactObservedDistance =
            findFieldTextExact(report.values, ['Distance Made Good (24 Hours) (NM)']) ||
            findFieldTextExact(report.values, ['Total Distance Made Good (NM)']) ||
            findFieldTextExact(report.values, ['Distance Observed (nm)']) ||
            findFieldTextExact(report.values, ['Distance Observed (NM)']) ||
            findFieldTextExact(report.values, ['Distance Observed (Nm)']);
        if (exactObservedDistance) {
            return parseFloat(exactObservedDistance) || 0;
        }

        const observedDistanceField = report.values.find((v) => {
            const label = v.field.label.toLowerCase().trim();
            return label.includes('distance observed') || label.includes('distance made good');
        });
        if (observedDistanceField) {
            return parseFloat(observedDistanceField.valueText) || 0;
        }

        const exactStoredDistance = findFieldTextExact(report.values, ['Distance Travelled']);
        if (exactStoredDistance) {
            return parseFloat(exactStoredDistance) || 0;
        }

        const exactDistanceField = report.values.find(v =>
            [
                'distance travelled',
                'distance traveled',
                'total distance travelled',
                'total distance traveled',
                'distance made good (24 hours) (nm)',
                'total distance made good (nm)',
            ]
                .includes(v.field.label.trim().toLowerCase().replace(/:$/, ''))
        );
        if (exactDistanceField) {
            return parseFloat(exactDistanceField.valueText) || 0;
        }

        const distanceField = report.values.find(v => {
            const label = v.field.label.toLowerCase().trim();
            return (
                label.includes('distance travelled') ||
                label.includes('distance traveled') ||
                label.includes('distance made good') ||
                label.includes('distance run') ||
                label.includes('run distance') ||
                label.includes('distance covered') ||
                (label.includes('distance') && label.includes('nm'))
            );
        });

        return distanceField ? parseFloat(distanceField.valueText) || 0 : null;
    };

  const getDistanceTravelledForDay = (reportsForDay: ReportDtoFull[]) => {
        for (const dayReport of reportsForDay) {
            const distance = getDistanceTravelledFromNoonReport(dayReport);
            if (distance !== null) {
                return distance;
            }
        }

        return 0;
    };

  const parseJsonArray = (raw?: string) => {
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

  const getMachineryBucket = (machineryRaw: unknown) => {
        const machinery = String(machineryRaw || '').trim().toLowerCase().replace(/[\s/_()-]+/g, '');
        if (!machinery) return '';
        if (machinery.startsWith('me')) return 'me';
        if (machinery.startsWith('ae')) return 'ae';
        if (machinery.startsWith('boiler')) return 'boiler';
        return '';
    };

  const normalizeFuelToken = (value: unknown) =>
        String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]+/g, ' ');

  const getConfiguredFuelCode = (fuelRaw: unknown) => {
        const fuel = normalizeFuelToken(fuelRaw);
        if (!fuel) return '';

        for (const factor of ciiRuleSet?.fuelFactors || []) {
            const aliases = String(factor.aliases || factor.fuelCode || '')
                .split(',')
                .map((alias) => normalizeFuelToken(alias))
                .filter(Boolean);
            if (normalizeFuelToken(factor.fuelCode) === fuel || aliases.includes(fuel)) {
                return factor.fuelCode;
            }
        }

        if (fuel === 'HFO' || fuel === 'HSFO') return 'HSFO';
        if (fuel === 'MGO' || fuel === 'MDO') return 'MGO';
        return '';
    };

  const getFuelBucket = (fuelRaw: unknown) => {
        const fuelCode = getConfiguredFuelCode(fuelRaw);
        if (fuelCode === 'HSFO') return 'hsfo';
        if (fuelCode === 'MGO') return 'mgo';
        return '';
    };

  const getOrCreateFuelBreakdown = (fuels: FuelConsumptionMap, fuelCode: string) => {
        if (!fuels[fuelCode]) {
            fuels[fuelCode] = { me: 0, ae: 0, boiler: 0, other: 0, rob: 0 };
        }
        return fuels[fuelCode];
    };

  const getOrCreateFuelCorrection = (corrections: FuelCorrectionMap, fuelCode: string) => {
        if (!corrections[fuelCode]) {
            corrections[fuelCode] = { fcElectrical: 0, fcBoiler: 0, fcOther: 0 };
        }
        return corrections[fuelCode];
    };

  const normalizeOperationType = (value: unknown) => {
        const normalized = normalizeTextForMatch(String(value || ''));
        if (!normalized) return '';
        if (normalized.includes('routine')) return 'routine';
        if ((normalized.includes('inert') || normalized.includes('intert')) && normalized.includes('heat')) return 'inerting_heating';
        if (normalized.includes('discharge') && normalized.includes('pump')) return 'discharge_pumps';
        if (normalized.includes('discharge')) return 'discharge_operations';
        return normalized.replace(/\s+/g, '_');
    };

  const normalizeMachineryForCorrection = (value: unknown) =>
        normalizeTextForMatch(String(value || '')).replace(/\s+/g, '');

  const isBoilerCorrectionMachinery = (machinery: unknown) => {
        const normalized = normalizeMachineryForCorrection(machinery);
        return normalized === 'boiler1' || normalized === 'boiler2';
    };

  const isAeMachinery = (machinery: unknown) => {
        const normalized = normalizeMachineryForCorrection(machinery);
        return ['ae1', 'ae2', 'ae3', 'ae4'].includes(normalized);
    };

  const isBoilerCorrectionOperation = (operationType: unknown) => {
        const normalized = normalizeOperationType(operationType);
        return normalized === 'inerting_heating' || normalized === 'discharge_operations';
    };

  const isAeDischargePumpCorrection = (machinery: unknown, operationType: unknown) =>
        isAeMachinery(machinery) && normalizeOperationType(operationType) === 'discharge_pumps';

  const isStandalonePumpOtherCorrection = (machinery: unknown, operationType: unknown) => {
        const normalizedMachinery = normalizeTextForMatch(String(machinery || ''));
        const normalizedOperation = normalizeOperationType(operationType);
        const isPump = normalizedMachinery.includes('pump') && (normalizedMachinery.includes('cargo') || normalizedMachinery.includes('discharge'));
        return isPump && (normalizedOperation === 'discharge_pumps' || normalizedOperation === 'discharge_operations');
    };

  const getNestedArrayValue = (rows: unknown[], rowIndex: number, itemIndex: number) => {
        const row = rows[rowIndex];
        return Array.isArray(row) ? row[itemIndex] : undefined;
    };

  const getFuelFactor = (fuelCode: string): CiiFuelFactorDto | undefined =>
        (ciiRuleSet?.fuelFactors || []).find((factor) => factor.fuelCode === fuelCode);

  const getFuelTotal = (fuel?: FuelConsumptionBreakdown) =>
        fuel ? fuel.me + fuel.ae + fuel.boiler + fuel.other : 0;

  const computeCo2FromFuelBreakdown = (fuels?: FuelConsumptionMap) => {
        if (!fuels) return 0;
        return Object.entries(fuels).reduce((acc, [fuelCode, fuel]) => {
            const factor = getFuelFactor(fuelCode);
            if (!factor) return acc;
            return acc + (getFuelTotal(fuel) * Number(factor.co2Factor || 0));
        }, 0);
    };

  const getCorrectionFactorProduct = () => {
        const factorValues = [
            Number(vesselCiiSetting?.factorFi || 1),
            Number(vesselCiiSetting?.factorFm || 1),
            Number(vesselCiiSetting?.factorFc || 1),
            Number(vesselCiiSetting?.factorFiVse || 1),
        ].map((value) => (Number.isFinite(value) && value > 0 ? value : 1));
        return factorValues.reduce((acc, value) => acc * value, 1);
    };

    const getCorrectedDistanceForRows = (rows: AggregatedVoyageData[]) => {
        const totalDistance = rows.reduce((acc, row) => acc + toFiniteNumber(row.distance), 0);
        const dx = rows.reduce((acc, row) => acc + numericValue(row.distanceAdjustment), 0);
        return Math.max(0, totalDistance - dx);
    };

  const computeCorrectedCo2ForRow = (row: AggregatedVoyageData) => {
        const stsVoyageIsY = resolveStsVoyageValue(row.stsVoyage) === 'Y';
        // Statutory SEEMP EF-026: When STS Voyage is 'Y', voyage event is exempt from Attained CII calculation (Corrected CO2 = 0)
        if (stsVoyageIsY) {
            return 0;
        }

        const boilerFactor = 0.75 - (0.03 * getShipTypeIndexByYear(selectedYear));

        if (row.fuelBreakdown && Object.keys(row.fuelBreakdown).length > 0) {
            return Object.entries(row.fuelBreakdown).reduce((acc, [fuelCode, fuel]) => {
                const factor = getFuelFactor(fuelCode);
                if (!factor) return acc;
                const fuelTotal = getFuelTotal(fuel);
                const fcVoyage =
                    fuelCode === 'HSFO'
                        ? numericValue(row.fcVoyageHsfo)
                        : fuelCode === 'MGO'
                            ? numericValue(row.fcVoyageMgo)
                            : 0;
                const tfExplicit =
                    fuelCode === 'HSFO'
                        ? numericValue(row.tfHsfo)
                        : fuelCode === 'MGO'
                            ? numericValue(row.tfMgo)
                            : 0;
                const fcElectrical = getCorrectionValueForFuel(row, fuelCode, 'fcElectrical');
                const fcOther = getCorrectionValueForFuel(row, fuelCode, 'fcOther');
                const fcBoiler = getCorrectionValueForFuel(row, fuelCode, 'fcBoiler');
                const correctedFuel = Math.max(0, fuelTotal - (fcVoyage + tfExplicit + (boilerFactor * (fcBoiler + fcElectrical + fcOther))));
                return acc + (correctedFuel * Number(factor.co2Factor || 0));
            }, 0);
        }

        const fcHsfo = row.meHsfo + row.aeHsfo + row.boilerHsfo;
        const fcMgo = row.meMgo + row.aeMgo + row.boilerMgo;
        const fcVoyageHsfo = numericValue(row.fcVoyageHsfo);
        const fcVoyageMgo = numericValue(row.fcVoyageMgo);
        const tfHsfo = numericValue(row.tfHsfo);
        const tfMgo = numericValue(row.tfMgo);
        const fcBoilerHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcBoiler');
        const fcBoilerMgo = getCorrectionValueForFuel(row, 'MGO', 'fcBoiler');
        const fcElectricalHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcElectrical');
        const fcElectricalMgo = getCorrectionValueForFuel(row, 'MGO', 'fcElectrical');
        const fcOtherHsfo = getCorrectionValueForFuel(row, 'HSFO', 'fcOther');
        const fcOtherMgo = getCorrectionValueForFuel(row, 'MGO', 'fcOther');
        const correctedHsfo = Math.max(0, fcHsfo - (fcVoyageHsfo + tfHsfo + (boilerFactor * (fcBoilerHsfo + fcElectricalHsfo + fcOtherHsfo))));
        const correctedMgo = Math.max(0, fcMgo - (fcVoyageMgo + tfMgo + (boilerFactor * (fcBoilerMgo + fcElectricalMgo + fcOtherMgo))));
        return (correctedHsfo * CARBON_FACTORS.HSFO) + (correctedMgo * CARBON_FACTORS.MGO);
    };

  const getConsumptionBreakdownForDay = (reportsForDay: ReportDtoFull[]): ConsumptionRobBreakdown | null => {
        let hasFuelRows = false;
        const breakdown: ConsumptionRobBreakdown = {
            meHsfo: 0,
            aeHsfo: 0,
            boilerHsfo: 0,
            meMgo: 0,
            aeMgo: 0,
            boilerMgo: 0,
            hfoRob: 0,
            mgoRob: 0,
            fuels: {},
            correctionsByFuel: {},
            unmappedFuelTypes: [],
        };

        for (const dayReport of reportsForDay) {
            if (isBunkerReport(dayReport)) continue;
            const fuelTypes = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'fuel type')?.valueText
            );
            const machineries = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'machinery')?.valueText
            );
            const consumedRows = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'consumed')?.valueText
            );
            const operationTypeRows = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase().includes('operation type'))?.valueText
            );
            const outerRobs = parseJsonArray(
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'last rob')?.valueText ||
                dayReport.values.find(v => v.field.label.trim().toLowerCase() === 'rob')?.valueText
            );

            if (!fuelTypes.length) {
                continue;
            }

            hasFuelRows = true;

            fuelTypes.forEach((fuelTypeRaw, fuelIndex) => {
                const fuelCode = getConfiguredFuelCode(fuelTypeRaw);
                const fuelBucket = getFuelBucket(fuelTypeRaw);
                if (!fuelCode) {
                    const rawFuel = String(fuelTypeRaw || '').trim();
                    if (rawFuel && !breakdown.unmappedFuelTypes?.includes(rawFuel)) {
                        breakdown.unmappedFuelTypes?.push(rawFuel);
                    }
                    return;
                }

                const machineryRow = Array.isArray(machineries[fuelIndex]) ? machineries[fuelIndex] : [];
                const consumedRow = Array.isArray(consumedRows[fuelIndex]) ? consumedRows[fuelIndex] : [];
                const outerRobValue = parseFloat(String(outerRobs[fuelIndex] || '0')) || 0;
                const fuelDetail = getOrCreateFuelBreakdown(breakdown.fuels || {}, fuelCode);
                fuelDetail.rob = outerRobValue;

                if (fuelBucket === 'hsfo' && outerRobValue > 0) breakdown.hfoRob = outerRobValue;
                if (fuelBucket === 'mgo' && outerRobValue > 0) breakdown.mgoRob = outerRobValue;

                machineryRow.forEach((machineryRaw: unknown, machineryIndex: number) => {
                    const machineryBucket = getMachineryBucket(machineryRaw);
                    const consumedValue = parseFloat(String(consumedRow[machineryIndex] || '0')) || 0;
                    const operationType = getNestedArrayValue(operationTypeRows, fuelIndex, machineryIndex);

                    if (machineryBucket) {
                        if (machineryBucket === 'me') fuelDetail.me += consumedValue;
                        if (machineryBucket === 'ae') fuelDetail.ae += consumedValue;
                        if (machineryBucket === 'boiler') fuelDetail.boiler += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'me') breakdown.meHsfo += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'ae') breakdown.aeHsfo += consumedValue;
                        if (fuelBucket === 'hsfo' && machineryBucket === 'boiler') breakdown.boilerHsfo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'me') breakdown.meMgo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'ae') breakdown.aeMgo += consumedValue;
                        if (fuelBucket === 'mgo' && machineryBucket === 'boiler') breakdown.boilerMgo += consumedValue;
                    } else if (isStandalonePumpOtherCorrection(machineryRaw, operationType)) {
                        fuelDetail.other += consumedValue;
                    }

                    const correction = getOrCreateFuelCorrection(breakdown.correctionsByFuel || {}, fuelCode);
                    if (isBoilerCorrectionMachinery(machineryRaw) && isBoilerCorrectionOperation(operationType)) {
                        correction.fcBoiler += consumedValue;
                    }
                    if (isAeDischargePumpCorrection(machineryRaw, operationType)) {
                        correction.fcElectrical += consumedValue;
                    }
                    if (isStandalonePumpOtherCorrection(machineryRaw, operationType)) {
                        correction.fcOther += consumedValue;
                    }
                });
            });
        }

        return hasFuelRows ? breakdown : null;
    };

  const getConsumptionBreakdownFromApi = (apiData: any): ConsumptionRobBreakdown | null => {
        const rows = Array.isArray(apiData?.rows)
            ? apiData.rows
            : Array.isArray(apiData?.details)
                ? apiData.details
                : Array.isArray(apiData)
                    ? apiData
                    : [];

        if (!rows.length) return null;

        const breakdown: ConsumptionRobBreakdown = {
            meHsfo: 0,
            aeHsfo: 0,
            boilerHsfo: 0,
            meMgo: 0,
            aeMgo: 0,
            boilerMgo: 0,
            hfoRob: 0,
            mgoRob: 0,
            fuels: {},
            correctionsByFuel: {},
            unmappedFuelTypes: [],
        };

        rows.forEach((row: any) => {
            const fuelCode = getConfiguredFuelCode(row?.fieldType);
            const fuelBucket = getFuelBucket(row?.fieldType);
            if (!fuelCode) {
                const rawFuel = String(row?.fieldType || '').trim();
                if (rawFuel && !breakdown.unmappedFuelTypes?.includes(rawFuel)) {
                    breakdown.unmappedFuelTypes?.push(rawFuel);
                }
                return;
            }

            const outerRobValue = parseFloat(String(row?.lastRob ?? row?.rob ?? 0)) || 0;
            const fuelDetail = getOrCreateFuelBreakdown(breakdown.fuels || {}, fuelCode);
            fuelDetail.rob = outerRobValue;
            if (fuelBucket === 'hsfo' && outerRobValue > 0) breakdown.hfoRob = outerRobValue;
            if (fuelBucket === 'mgo' && outerRobValue > 0) breakdown.mgoRob = outerRobValue;

            const machineries = Array.isArray(row?.machineries) ? row.machineries : [];
            machineries.forEach((mach: any) => {
                const machineryBucket = getMachineryBucket(mach?.machinery);
                const consumedValue = parseFloat(String(mach?.consumed ?? 0)) || 0;
                const operationType = mach?.operationType;

                if (machineryBucket) {
                    if (machineryBucket === 'me') fuelDetail.me += consumedValue;
                    if (machineryBucket === 'ae') fuelDetail.ae += consumedValue;
                    if (machineryBucket === 'boiler') fuelDetail.boiler += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'me') breakdown.meHsfo += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'ae') breakdown.aeHsfo += consumedValue;
                    if (fuelBucket === 'hsfo' && machineryBucket === 'boiler') breakdown.boilerHsfo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'me') breakdown.meMgo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'ae') breakdown.aeMgo += consumedValue;
                    if (fuelBucket === 'mgo' && machineryBucket === 'boiler') breakdown.boilerMgo += consumedValue;
                } else if (isStandalonePumpOtherCorrection(mach?.machinery, operationType)) {
                    fuelDetail.other += consumedValue;
                }

                const correction = getOrCreateFuelCorrection(breakdown.correctionsByFuel || {}, fuelCode);
                if (isBoilerCorrectionMachinery(mach?.machinery) && isBoilerCorrectionOperation(operationType)) {
                    correction.fcBoiler += consumedValue;
                }
                if (isAeDischargePumpCorrection(mach?.machinery, operationType)) {
                    correction.fcElectrical += consumedValue;
                }
                if (isStandalonePumpOtherCorrection(mach?.machinery, operationType)) {
                    correction.fcOther += consumedValue;
                }
            });
        });

        return breakdown;
    };

  const processReportData = async (
        reports: ReportDtoFull[],
        bunkerReports: ReportDtoFull[],
        cargoData: any[],
        dprRecs: any[],
        voyages: any[] = [],
        ciiRowsByReportId: Record<number, CiiVoyageRowDto> = {}
    ) => {
        let activeVoyageNo = '';
        let currentEventState: VoyageEventState = 'SAILING';
        let pendingDepartureResolution = false;
        let lastReportDateTime = '';
        const { prevByVoyageNo } = buildVoyageSequence(voyages || []);
        const combinedBunkerReports = Array.from(
            new Map(
                [...bunkerReports, ...reports]
                    .filter((report) => isBunkerReport(report))
                    .map((report) => [String(report.id ?? `${report.reportType}-${report.createdDateTime}`), report])
            ).values()
        );
        const sourceReports = [...reports]
            .filter(r => !isBunkerReport(r) && isPositionReportCandidate(r))
            .sort((a, b) => {
                const aTime = toDateTimeMs(getReportSortDateTime(a));
                const bTime = toDateTimeMs(getReportSortDateTime(b));
                if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) return aTime - bTime;
                return (getReportSortDateTime(a) || '').localeCompare(getReportSortDateTime(b) || '');
            });

        const processed: AggregatedVoyageData[] = [];

          for (const voyageSourceReport of sourceReports) {
              const reportDateTime =
                  getDateTimeFieldValueFromReport(voyageSourceReport, ['report date time']) ||
                  getDateTimeFieldValueFromReport(voyageSourceReport) ||
                  getReportSortDateTime(voyageSourceReport);
              const reportDate = getReportDateOnly(voyageSourceReport) || toDateOnly(reportDateTime);
              const fullReportsForDay = combinedBunkerReports.filter(r => getReportDateOnly(r) === reportDate);
            const dayReports = fullReportsForDay.length
                ? Array.from(
                    new Map(
                        [voyageSourceReport, ...fullReportsForDay]
                            .filter(Boolean)
                            .map((report) => [String(report.id ?? `${report.reportType}-${report.createdDateTime}`), report])
                    ).values()
                )
                : [voyageSourceReport];
            const dprDay = dprRecs.find((d: any) => d.reportDate === reportDate);
              const voyageNumberFromReport = findFieldText(voyageSourceReport.values || [], ['voyage no', 'voyage number']);
              const explicitVoyageNo = voyageNumberFromReport || dprDay?.voyageNo || '';
            const voyageByNumber = explicitVoyageNo
                ? (voyages || []).find((v: any) =>
                    String(v?.voyageNumber || '').trim().toUpperCase() === String(explicitVoyageNo).trim().toUpperCase()
                )
                : null;
            const matchingVoyage = voyageByNumber || findMatchingVoyageByDate(reportDateTime || reportDate, voyages || []);

            const consumptionApiData = voyageSourceReport?.id && matchingVoyage?.id
                ? await getConsumptionByVoyageReport(matchingVoyage.id, voyageSourceReport.id).catch(() => null)
                : null;
              const consumptionBreakdown =
                  getConsumptionBreakdownFromApi(consumptionApiData) ||
                  getConsumptionBreakdownForDay(dayReports);
              const persistedCiiRow =
                  voyageSourceReport?.id != null
                      ? ciiRowsByReportId[Number(voyageSourceReport.id)]
                      : undefined;
              const persistedStsVoyage = persistedCiiRow?.stsVoyage;
              const persistedBoilerUsed = persistedCiiRow?.boilerUsed;
              const persistedFcVoyageHsfo = parseOptionalNumber(persistedCiiRow?.fcVoyageHsfo);
              const persistedFcVoyageMgo = parseOptionalNumber(persistedCiiRow?.fcVoyageMgo);
                const persistedHsfoSulphur = parseOptionalNumber(persistedCiiRow?.hsfoSulphur);
                const persistedMgoSulphur = parseOptionalNumber(persistedCiiRow?.mgoSulphur);
              const persistedHsfoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'HSFO');
              const persistedMgoFuelDetail = getPersistedFuelDetail(persistedCiiRow, 'MGO');
              const persistedDistanceAdjustment = parseOptionalNumber(persistedCiiRow?.distanceAdjustment);
              const correctionData = buildCorrectionBreakdownForRow(consumptionBreakdown?.correctionsByFuel, persistedCiiRow);
              const persistedRemarks = persistedCiiRow?.remarks || '';

            const findValue = (labels: string[]) => {
                const match = voyageSourceReport.values.find(v => labels.some(l => v.field.label.toLowerCase().includes(l.toLowerCase())));
                return match ? parseFloat(match.valueText) || 0 : 0;
            };

            const meHsfo = consumptionBreakdown?.meHsfo ?? findValue(['ME HSFO', 'ME HFO', 'ME LSFO', 'ME FO']);
            const aeHsfo = consumptionBreakdown?.aeHsfo ?? findValue(['AE HSFO', 'AE HFO', 'AE LSFO', 'AE FO']);
            const boilerHsfo = consumptionBreakdown?.boilerHsfo ?? findValue(['Boiler HSFO', 'Boiler HFO', 'Boiler FO']);
            const meMgo = consumptionBreakdown?.meMgo ?? findValue(['ME MGO', 'ME MDO', 'ME Diesel']);
            const aeMgo = consumptionBreakdown?.aeMgo ?? findValue(['AE MGO', 'AE MDO', 'AE Diesel']);
            const boilerMgo = consumptionBreakdown?.boilerMgo ?? findValue(['Boiler MGO', 'Boiler MDO', 'Boiler Diesel']);

            const totalHsfo = meHsfo + aeHsfo + boilerHsfo;
            const totalMgo = meMgo + aeMgo + boilerMgo;
            const fuelBreakdown = consumptionBreakdown?.fuels;
            const dynamicTotalFuel = fuelBreakdown
                ? Object.values(fuelBreakdown).reduce((acc, fuel) => acc + getFuelTotal(fuel), 0)
                : 0;
            const dynamicTotalCo2 = computeCo2FromFuelBreakdown(fuelBreakdown);
            const totalFuel = dynamicTotalFuel || totalHsfo + totalMgo || (dprDay?.foConsumptionRows?.length > 0 ? parseFloat(dprDay.foConsumptionRows[0].totalFoCons) || 0 : 0);
            const totalCo2 = dynamicTotalCo2 || (totalHsfo * CARBON_FACTORS.HSFO) + (totalMgo * CARBON_FACTORS.MGO) || (totalFuel * CARBON_FACTORS.HSFO);

            let bunkerReceivedHfo = 0;
            let bunkerReceivedMgo = 0;
            let hfoRob = consumptionBreakdown?.hfoRob || 0;
            let mgoRob = consumptionBreakdown?.mgoRob || 0;
            let bunkerReportName = '';

            const bunkerReport = fullReportsForDay.find(r => isBunkerReport(r));
            if (bunkerReport) {
                bunkerReportName = bunkerReport.reportType || 'Bunker Report';
                const bunkerRowsField = bunkerReport.values.find(v => v.field.label === 'Bunker Received Rows');
                // Do not override ROB from bunker report fields; ROB should come from the current report's Consumption/ROB.

                if (bunkerRowsField?.valueText) {
                    try {
                        const rows = JSON.parse(bunkerRowsField.valueText);
                        if (Array.isArray(rows)) {
                              rows.forEach((row: any) => {
                                  const genericSulphurValue = parseFloat(String(row?.sulfurPercentage ?? ''));
                                  bunkerReceivedHfo += parseFloat(row.hfo) || 0;
                                  bunkerReceivedMgo += parseFloat(row.mgo) || 0;
                                  if (!isNaN(genericSulphurValue)) {
                                      // sulphur content is now provided directly in CII voyage rows
                                  }
                              });
                          }
                    } catch (e) {
                        console.error('Failed to parse bunker received rows', e);
                    }
                }
            }

            if (bunkerReceivedHfo === 0 && bunkerReceivedMgo === 0) {
                bunkerReceivedHfo = dprDay?.robStatus?.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('fuel') || c.includes('hsfo') || c.includes('hfo') || c.includes('lsfo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.received) || 0), 0) || 0;

                bunkerReceivedMgo = dprDay?.robStatus?.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('mgo') || c.includes('diesel') || c.includes('mdo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.received) || 0), 0) || 0;
            }

            if (hfoRob === 0 && mgoRob === 0 && dprDay?.robStatus) {
                hfoRob = dprDay.robStatus.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('fuel') || c.includes('hsfo') || c.includes('hfo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.rob) || 0), 0) || 0;
                mgoRob = dprDay.robStatus.filter((r: any) => {
                    const c = r.commodity?.toLowerCase() || '';
                    return c.includes('mgo') || c.includes('diesel') || c.includes('mdo');
                }).reduce((acc: number, r: any) => acc + (parseFloat(r.rob) || 0), 0) || 0;
            }

            const cargoWeight = getCargoWeightFromReport(voyageSourceReport);
            const vesselCondition = getVesselConditionFromReport(voyageSourceReport, matchingVoyage?.loadingCondition);
            const rawEventType = getEventTypeFromReport(voyageSourceReport);
            const reportKind = getReportKindFromReport(voyageSourceReport);
            const resolvedState = resolveRowAndNextEventState(
                reportKind,
                currentEventState,
                voyageSourceReport,
                rawEventType
            );
            const primaryReportDateTime =
                buildReportUtcDateTime(voyageSourceReport) ||
                reportDateTime ||
                reportDate ||
                '';
            const departureBoundaryDateTime = primaryReportDateTime;
            const arrivalBoundaryDateTime = primaryReportDateTime;
            let rowState: VoyageEventState =
                reportKind === 'COSP'
                    ? getDeparturePrevStateFromEventFlags(voyageSourceReport, currentEventState)
                    : resolvedState.rowState;
            let nextState: VoyageEventState =
                reportKind === 'COSP'
                    ? 'SAILING'
                    : resolvedState.nextState;

            if (pendingDepartureResolution && reportKind !== 'DEPARTURE') {
                if (reportKind === 'ARRIVAL') {
                    const arrivalState = getArrivalNextStateFromEventFlags(
                        voyageSourceReport,
                        rawEventType,
                        currentEventState
                    );
                    rowState = arrivalState;
                    nextState = arrivalState;
                } else {
                    rowState = 'SAILING';
                    nextState = 'SAILING';
                }
                pendingDepartureResolution = false;
            }

            if (reportKind === 'DEPARTURE') {
                rowState = currentEventState;
                nextState = 'SAILING';
                pendingDepartureResolution = false;
            }

                const departurePortLocation =
                    getPortValueFromReport(voyageSourceReport.values || [], ['departure port', 'from location', 'port name', 'location']) ||
                    dprDay?.fromLocation ||
                    dprDay?.dlLocation ||
                    matchingVoyage?.departurePort ||
                    '';
                const arrivalPortLocation =
                    getPortValueFromReport(voyageSourceReport.values || [], ['arrival port', 'to location', 'port name', 'location']) ||
                    dprDay?.toLocation ||
                    matchingVoyage?.arrivalPort ||
                    departurePortLocation;

            const portNameLocation =
                rowState === 'SAILING'
                    ? departurePortLocation
                    : rowState === 'AT_BERTH' || rowState === 'AT_ANCHOR'
                        ? arrivalPortLocation
                        : departurePortLocation;

            const foundVoyageNo =
                voyageNumberFromReport ||
                dprDay?.voyageNo ||
                activeVoyageNo ||
                matchingVoyage?.voyageNumber ||
                '';
            const reportVoyageNo = foundVoyageNo || activeVoyageNo || '';
            const previousVoyageFallback =
                activeVoyageNo && activeVoyageNo !== reportVoyageNo
                    ? activeVoyageNo
                    : '';
            const previousVoyageNo = reportVoyageNo
                ? (prevByVoyageNo.get(reportVoyageNo) || previousVoyageFallback || '')
                : '';
            const rowVoyageNo =
                reportKind === 'COSP'
                    ? (previousVoyageNo || '')
                    : (reportVoyageNo || '');

            if (reportKind === 'COSP') {
                if (reportVoyageNo) {
                    activeVoyageNo = reportVoyageNo;
                }
            } else if (reportVoyageNo) {
                activeVoyageNo = reportVoyageNo;
            }

            const isBoundaryReport = reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE';
            const metricsAllowed = !(reportKind === 'COSP' && !previousVoyageNo);
            if (metricsAllowed && rowVoyageNo) {
                const rowToDateTime = primaryReportDateTime || reportDateTime || reportDate;
                let metricsDateTime = lastReportDateTime || rowToDateTime;
                let metricsToDateTime = rowToDateTime;

                if (reportKind === 'ARRIVAL' && arrivalBoundaryDateTime) {
                    metricsToDateTime = arrivalBoundaryDateTime;
                } else if ((reportKind === 'DEPARTURE' || reportKind === 'COSP' || reportKind === 'EOSP') && departureBoundaryDateTime) {
                    metricsToDateTime = departureBoundaryDateTime;
                }

                const computedHours = getHoursByEventState(rowState, metricsDateTime, metricsToDateTime);
                    processed.push({
                        fromDate: metricsDateTime || '',
                        toDate: metricsToDateTime || '',
                        sourceDateTime: metricsDateTime || '',
                        distance: getDistanceTravelledFromNoonReport(voyageSourceReport) ?? getDistanceTravelledForDay(dayReports),
                        hoursAtSea: computedHours.hoursAtSea,
                        hoursAtAnchor: computedHours.hoursAtAnchor,
                        cargoCarried: isBoundaryReport ? 0 : (cargoWeight ?? 0),
                        cargoWeightProvided: !isBoundaryReport && cargoWeight !== null,
                        meHsfo,
                        aeHsfo,
                        boilerHsfo,
                        meMgo,
                        aeMgo,
                        boilerMgo,
                        totalFuel,
                        totalCo2,
                        bunkerReceivedHfo,
                        bunkerReceivedMgo,
                        fuelBreakdown,
                        correctionBreakdown: correctionData.corrections,
                        correctionSourceByFuel: correctionData.sources,
                        unmappedFuelTypes: consumptionBreakdown?.unmappedFuelTypes || [],
                        reportName: voyageSourceReport.reportType || '--',
                        bunkerReportName: bunkerReportName || '--',
                        hfoRob,
                        mgoRob,
                        cargoHandled: 0,
                        fcVoyageHsfo:
                            persistedFcVoyageHsfo ??
                            (voyageSourceReport ? (parseFloat(findFieldTextExact(voyageSourceReport.values || [], ['FC voyage HSFO'])) || undefined) : undefined),
                        fcVoyageMgo:
                            persistedFcVoyageMgo ??
                            (voyageSourceReport ? (parseFloat(findFieldTextExact(voyageSourceReport.values || [], ['FC voyage MGO'])) || undefined) : undefined),
                        fcElectricalHsfo: correctionData.corrections.HSFO?.fcElectrical,
                        fcElectricalMgo: correctionData.corrections.MGO?.fcElectrical,
                        fcBoilerHsfo: correctionData.corrections.HSFO?.fcBoiler,
                        fcBoilerMgo: correctionData.corrections.MGO?.fcBoiler,
                        fcOtherHsfo: correctionData.corrections.HSFO?.fcOther,
                        fcOtherMgo: correctionData.corrections.MGO?.fcOther,
                        tfHsfo: parseOptionalNumber(persistedHsfoFuelDetail?.fcVoyageAdjustment),
                        tfMgo: parseOptionalNumber(persistedMgoFuelDetail?.fcVoyageAdjustment),
                        distanceAdjustment: persistedDistanceAdjustment,
                        reeferKwh: parseOptionalNumber(persistedCiiRow?.reeferKwh),
                        reeferSfoc: parseOptionalNumber(persistedCiiRow?.reeferSfoc),
                        reeferFuelCode: persistedCiiRow?.reeferFuelCode || '',
                        reeferContainersOnboard: hasFieldWithExactLabel(voyageSourceReport?.values || [], ['Refrigerated Containers Onboard'])
                            ? getCheckboxFieldState(voyageSourceReport, ['Refrigerated Containers Onboard'])
                            : true,
                        cargoCoolingSystemInUse: hasFieldWithExactLabel(voyageSourceReport?.values || [], ['Cargo Cooling System In Use'])
                            ? getCheckboxFieldState(voyageSourceReport, ['Cargo Cooling System In Use'])
                            : false,
                        vesselCondition,
                        portNameLocation,
                        eventType: toVoyageEventLabel(rowState),
                        reportKind,
                        reportDateTime: reportDateTime || reportDate,
                        departureDateTime: departureBoundaryDateTime || '',
                        arrivalDateTime: arrivalBoundaryDateTime || '',
                        departurePortLocation,
                        arrivalPortLocation,
                        anchorArrival: getArrivalNextStateFromEventFlags(voyageSourceReport, rawEventType, currentEventState) === 'AT_ANCHOR',
                        departureFromState: getDeparturePrevStateFromEventFlags(voyageSourceReport, currentEventState),
                        vesselId: matchingVoyage?.vessel?.id,
                        stsVoyage: persistedStsVoyage || findFieldText(voyageSourceReport.values || [], ['sts voyage']) || '',
                        boilerUsed: persistedBoilerUsed || '',
                        remarks: persistedRemarks,
                        voyageNo: rowVoyageNo,
                        leg: resolveLegDisplay(voyageSourceReport, matchingVoyage, persistedCiiRow),
                        avgSulphurHsfo: persistedHsfoSulphur,
                        avgSulphurMgo: persistedMgoSulphur,
                        reportId: voyageSourceReport.id,
                    reportAssignmentId: voyageSourceReport.assignment?.id,
                    reportVoyageId: voyageSourceReport.voyage,
                    reportTypeName: voyageSourceReport.reportType,
                    reportStatus: voyageSourceReport.status,
                    reportHasWarnings: voyageSourceReport.hasWarnings,
                    reportNoOfWarnings: voyageSourceReport.noOfWarnings,
                });
            }

            const shouldCreateNextEventRow =
                (reportKind === 'COSP' || reportKind === 'ARRIVAL' || reportKind === 'DEPARTURE') &&
                rowState !== nextState;

            if (shouldCreateNextEventRow) {
                const nextVoyageNo = reportVoyageNo || '';
                if (nextVoyageNo) {
                    const boundaryState = nextState;
                    const boundaryDateTime =
                        (boundaryState === 'SAILING' ? departureBoundaryDateTime : arrivalBoundaryDateTime) ||
                        primaryReportDateTime ||
                        reportDateTime ||
                        reportDate ||
                        '';
                    const boundarySourceTime = boundaryDateTime || primaryReportDateTime || reportDateTime || reportDate;

                    processed.push({
                        fromDate: boundaryDateTime,
                        toDate: '',
                        sourceDateTime: boundarySourceTime || '',
                        distance: 0,
                        hoursAtSea: 0,
                        hoursAtAnchor: 0,
                        cargoCarried: cargoWeight ?? 0,
                        cargoWeightProvided: cargoWeight !== null,
                        meHsfo: 0,
                        aeHsfo: 0,
                        boilerHsfo: 0,
                        meMgo: 0,
                        aeMgo: 0,
                        boilerMgo: 0,
                        totalFuel: 0,
                        totalCo2: 0,
                        bunkerReceivedHfo: 0,
                        bunkerReceivedMgo: 0,
                        reportName: voyageSourceReport.reportType || '--',
                        bunkerReportName: '--',
                        hfoRob: 0,
                        mgoRob: 0,
                        cargoHandled: 0,
                        vesselCondition: getVesselConditionFromReport(voyageSourceReport, matchingVoyage?.loadingCondition),
                        portNameLocation:
                            boundaryState === 'SAILING'
                                ? departurePortLocation
                                : arrivalPortLocation,
                        eventType: toVoyageEventLabel(boundaryState),
                        reportKind,
                        stsVoyage: '',
                        boilerUsed: '',
                        voyageNo: nextVoyageNo,
                        leg: resolveLegDisplay(voyageSourceReport, matchingVoyage, persistedCiiRow),
                        avgSulphurHsfo: undefined,
                        avgSulphurMgo: undefined,
                        isBoundaryRow: true,
                    } as AggregatedVoyageData);
                }
            }

            if (primaryReportDateTime) {
                lastReportDateTime = primaryReportDateTime;
            } else if (reportDateTime) {
                lastReportDateTime = reportDateTime;
            }

            currentEventState = nextState;
        }

        // Average sulphur content is provided directly in CII voyage rows.

        processed.forEach((row) => {
            row.cargoHandled = 0;
        });

        (cargoData || []).forEach((cargo: any) => {
            const cargoDateTimeRaw = cargo?.createdDateTime || cargo?.date || '';
            const cargoTime = new Date(cargoDateTimeRaw).getTime();
            if (Number.isNaN(cargoTime)) return;

            const cargoQty = Number(cargo?.totalCargoQtyMt) || 0;
            if (cargoQty <= 0) return;

            const matchingRow = processed.find((row, index) => {
                const fromTime = new Date(row.fromDate).getTime();
                if (Number.isNaN(fromTime)) return false;

                const toCandidate = row.toDate || processed[index + 1]?.fromDate || '';
                const toTime = toCandidate ? new Date(toCandidate).getTime() : NaN;

                if (!Number.isNaN(toTime)) {
                    return cargoTime >= fromTime && cargoTime <= toTime;
                }

                return toDateOnly(cargoDateTimeRaw) === toDateOnly(row.fromDate);
            });

            if (matchingRow) {
                matchingRow.cargoHandled += cargoQty;
            }
        });

        // ROB should reflect the current report's last ROB at the row's TO time.
        // Do not recompute rolling ROB from previous rows.

        const mergedRows = mergeVoyageRowsByEvent(processed);
        const splitRows = splitRowsOnQuarterBoundaries(mergedRows, reports, selectedYear);
        const voyageStartByNo = new Map<string, string>();
        (voyages || []).forEach((v: any) => {
            const voyageNo = String(v?.voyageNumber || '').trim();
            if (!voyageNo) return;
            if (!voyageStartByNo.has(voyageNo)) {
                voyageStartByNo.set(voyageNo, String(v?.startDate || ''));
            }
        });

        const orderedRows = [...splitRows].sort((a, b) => {
            const aKey = voyageStartByNo.get(a.voyageNo || '') || '';
            const bKey = voyageStartByNo.get(b.voyageNo || '') || '';
            if (aKey !== bKey) {
                return aKey.localeCompare(bKey);
            }
            const aTime = toDateTimeMs(a.fromDate);
            const bTime = toDateTimeMs(b.fromDate);
            if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
                return aTime - bTime;
            }
            return (a.fromDate || '').localeCompare(b.fromDate || '');
        });

        setAggregatedData(orderedRows);
    };
  const { rule, capacity, requiredCii } = getCiiRuleMetrics()
  const enriched = await enrichCreatedReportsWithAssignmentLabels(scopedReports)
  const fullForVessel = fullReports.filter(r => Number(r.vesselId ?? r.vesselDto?.id ?? r.vessel?.id) === selectedVesselId)
  const fullById = new Map(fullForVessel.map(r => [Number(r.id), r]))
  const reports: ReportDtoFull[] = enriched.map(report => {
    const values = fullById.get(Number(report.id))?.values?.length ? fullById.get(Number(report.id)).values : report.values
    if (!Array.isArray(values) || values.some(value => typeof value?.field?.label !== 'string')) throw new Error('Some saved Position Report fields could not be read. Check the vessel reports and retry.')
    return { ...report, values }
  }).filter(report => {
    const date = toIsoDateOnly(buildReportUtcDateTime(report) || report.submittedDateTime || report.createdDateTime)
    return Boolean(date) && date >= from && date <= to
  }).sort((a, b) => {
    const aTime = toDateTimeMs(getReportSortDateTime(a)); const bTime = toDateTimeMs(getReportSortDateTime(b))
    return Number.isFinite(aTime) && Number.isFinite(bTime) ? aTime - bTime : (getReportSortDateTime(a) || '').localeCompare(getReportSortDateTime(b) || '')
  })
  if (sourceFailures.length) throw new Error(`CII could not be calculated: ${sourceFailures[0].message}`)
  const saved = reports.length ? await getCiiVoyageRowsByReportIds(reports.map(r => Number(r.id)).filter(id => Number.isFinite(id) && id > 0)) : []
  if (!Array.isArray(saved)) throw new Error('The saved CII correction data is invalid. Retry loading the dashboard.')
  const savedById: Record<number, CiiVoyageRowDto> = {}
  const reportIds = new Set(reports.map(r => Number(r.id)))
  saved.filter(row => reportIds.has(Number(row.reportId)) && (row.vesselId == null || Number(row.vesselId) === selectedVesselId))
    .forEach(row => { savedById[Number(row.reportId)] = row })
  await processReportData(reports, fullForVessel,
    input.cargo.filter(r => Number(r.vesselId) === selectedVesselId), input.dpr.filter(r => Number(r.vesselId) === selectedVesselId || (!r.vesselId && r.vesselName === input.vessel.fleet_name)),
    input.voyages.filter(r => Number(r.vessel?.id) === selectedVesselId), savedById)
  if (sourceFailures.length) throw new Error(`CII could not be calculated: ${sourceFailures[0].message}`)
  const annualRows = aggregatedData.filter(row => row.fromDate >= from && row.fromDate <= to)
  const totalCo2 = annualRows.reduce((acc, row) => acc + computeCorrectedCo2ForRow(row), 0)
  const annualCapacity = capacity || Number(generalInfo?.summerDeadweight) || 50000
  const attainedCii = calculateAnnualAttainedCii(totalCo2, annualCapacity, getCorrectedDistanceForRows(annualRows), getCorrectionFactorProduct())
  const rating = getFinalCalculatedCiiRating(attainedCii, requiredCii, rule)
  const asOf = reports.filter(r => !isBunkerReport(r) && isPositionReportCandidate(r)).map(r => buildReportUtcDateTime(r) || r.submittedDateTime || r.createdDateTime).filter(Boolean).sort((a, b) => toDateTimeMs(a) - toDateTimeMs(b)).pop() || null
  return { ...result, rating, attainedCii, asOf }
}
